import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserGroupIcon, StarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const DEFAULT_ADDITIONAL_SPONSORSHIP_AVAILABILITY: Record<string, number> = {
  'beverage-cart': 6,
  'bloody-mary': 6,
  'food-truck': 2,
  'air-cannon': 1,
  'cigar-bar': 1,
  photography: 1,
  'raffle-prize': 4,
  'beverage-station': 4,
  'golf-cart': 1,
  'premier-hole': 10,
  'driving-range': 4,
  'closest-pin': 4,
  'longest-drive': 4,
};

const RegistrationPage: React.FC = () => {
  const MODAL_SWITCH_DELAY_MS = 4000;
  const location = useLocation();
  const navigate = useNavigate();
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [sponsorModalPlanType, setSponsorModalPlanType] = useState<'sponsor' | 'sponsor-golfer'>('sponsor');
  const [isSwitchingSponsorModal, setIsSwitchingSponsorModal] = useState(false);
  const [selectedSponsorTier, setSelectedSponsorTier] = useState<string | null>(null);
  const [selectedAdditionalSponsorships, setSelectedAdditionalSponsorships] = useState<string[]>([]);
  const [showAlaCarteModal, setShowAlaCarteModal] = useState(false);
  const [addGolfers, setAddGolfers] = useState<boolean | null>(null);
  const [showSponsorForm, setShowSponsorForm] = useState(false);
  const [confirmedSponsorTier, setConfirmedSponsorTier] = useState<any>(null);
  const [confirmedAdditionalSponsorships, setConfirmedAdditionalSponsorships] = useState<string[]>([]);
  const [confirmedAlaCarteItem, setConfirmedAlaCarteItem] = useState<any>(null);
  const [confirmedAlaCarteQuantity, setConfirmedAlaCarteQuantity] = useState<any>(null);
  const [selectedAlaCarteItem, setSelectedAlaCarteItem] = useState<string | null>(null);
  const [showAlaCarteSecondary, setShowAlaCarteSecondary] = useState(false);
  const [selectedQuantityOption, setSelectedQuantityOption] = useState<string | null>(null);
  const [currentAlaCarteType, setCurrentAlaCarteType] = useState<string | null>(null);
  const [contactInfo, setContactInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [smsUpdates, setSmsUpdates] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    companyName: '',
    logoFile: null as File | null,
    logoUrl: ''
  });
  const [silentAuctionData, setSilentAuctionData] = useState({
    amount: '',
    description: '',
    images: [] as File[]
  });
  const [showSilentAuctionForm, setShowSilentAuctionForm] = useState(false);
  const [monetaryDonationAmount, setMonetaryDonationAmount] = useState('');
  const [showMonetaryDonationForm, setShowMonetaryDonationForm] = useState(false);
  const [showAddGolfersForm, setShowAddGolfersForm] = useState(false);
  const [showDinnerOnlyForm, setShowDinnerOnlyForm] = useState(false);
  const [showGolfPlayersContent, setShowGolfPlayersContent] = useState(false);
  const [showDinnerGuestsContent, setShowDinnerGuestsContent] = useState(false);
  const [usePrimaryAsPlayer1, setUsePrimaryAsPlayer1] = useState(false);
  const [primaryPlayerNeedsGolfClubs, setPrimaryPlayerNeedsGolfClubs] = useState<'' | 'yes' | 'no'>('');
  const [primaryPlayerGolfClubHandedness, setPrimaryPlayerGolfClubHandedness] = useState<'' | 'left' | 'right'>('');
  const [teamName, setTeamName] = useState('');
  const [partyName, setPartyName] = useState('');
  const [players, setPlayers] = useState<Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    needsGolfClubs: '' | 'yes' | 'no';
    golfClubHandedness: '' | 'left' | 'right';
  }>>([]);
  const [dinnerGuests, setDinnerGuests] = useState<Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  }>>([]);
  const [usePrimaryAsGuest1, setUsePrimaryAsGuest1] = useState(false);
  const [specialNotes, setSpecialNotes] = useState('');
  const [showRemainingGolfersModal, setShowRemainingGolfersModal] = useState(false);
  const [remainingModalType, setRemainingModalType] = useState<'golfer' | 'dinner'>('golfer');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [sponsorModalToastMessage, setSponsorModalToastMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [additionalSponsorshipAvailability, setAdditionalSponsorshipAvailability] = useState<Record<string, number>>({});
  const [isLoadingSponsorshipAvailability, setIsLoadingSponsorshipAvailability] = useState(false);

  const nameRegex = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.(com|org|net)$/i;
  const phoneRegex = /^\(\d{3}\)\s\d{3}-\d{4}$/;

  // Phone number formatting function
  const formatPhoneNumber = (value: string) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length >= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    } else if (phoneNumber.length >= 3) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return phoneNumber;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setContactInfo(prev => ({ ...prev, phone: formatted }));
  };

  // Handle company logo upload
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('File selected:', file.name, file.size, file.type);

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a PNG, JPG, SVG, or WebP file.');
      e.target.value = '';
      return;
    }

    // Validate file size (maximum 3MB)
    const maxSize = 3 * 1024 * 1024; // 3MB in bytes
    if (file.size > maxSize) {
      alert('File size must be 3MB or smaller. Please choose a smaller file or compress your image.');
      e.target.value = '';
      return;
    }

    // Show file immediately for better UX, before upload
    setCompanyInfo(prev => ({ ...prev, logoFile: file }));

    // Upload company logo to /uploads/logos/ folder
    const formData = new FormData();
    formData.append('logo', file);

    try {
      console.log('Starting upload...');
      const response = await fetch('/api/upload-logo', {
        method: 'POST',
        body: formData
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error('Upload failed with status:', response.status, errorData);
        throw new Error(`Upload failed: ${response.status} ${errorData}`);
      }
      
      const result = await response.json();
      console.log('Logo uploaded successfully:', result);
      setCompanyInfo(prev => ({
        ...prev,
        logoUrl: result.fileUrl || ''
      }));
      
    } catch (error) {
      console.error('Error uploading logo:', error);
      // Keep the file in state for preview, but show a warning
      console.warn('Upload failed but file preview is available');
      // Remove the alert since file preview still works
    }
  };
  
  // Get the selection from navigation state
  const getSelectionName = () => {
    const state = location.state as { selectedPlan?: string; planType?: string } | null;
    
    if (state?.selectedPlan) {
      return state.selectedPlan.toUpperCase();
    }
    
    if (state?.planType) {
      switch (state.planType) {
        case 'sponsor':
        case 'sponsor-golfer':
          return state.planType === 'sponsor-golfer' ? 'SPONSOR + GOLFER' : 'SPONSOR ONLY';
        case 'al-a-carte':
        case 'ala-carte':
          return 'À LA CARTE';
        default:
          return 'SPONSOR';
      }
    }
    
    return 'SPONSOR';
  };

  // Check if this is a sponsor registration that should show the modal
  const shouldShowSponsorModal = useCallback(() => {
    const state = location.state as { planType?: string; returnFromBilling?: boolean } | null;
    if (state?.returnFromBilling) return false;
    return state?.planType === 'sponsor' || state?.planType === 'sponsor-golfer';
  }, [location.state]);

  // Check if this is an à la carte registration that should show the modal
  const shouldShowAlaCarteModal = useCallback(() => {
    const state = location.state as { planType?: string; returnFromBilling?: boolean } | null;
    if (state?.returnFromBilling) return false;
    return state?.planType === 'al-a-carte' || state?.planType === 'ala-carte';
  }, [location.state]);

  const TOP_TIER_SPONSOR_IDS = ['platinum', 'dinner', 'lunch'];
  const isTopTierSelection = selectedSponsorTier !== null && TOP_TIER_SPONSOR_IDS.includes(selectedSponsorTier);
  const hasAnySponsorSelection = selectedSponsorTier !== null || selectedAdditionalSponsorships.length > 0;
  const shouldShowSponsorGolferWarning =
    sponsorModalPlanType === 'sponsor-golfer'
    && !isTopTierSelection
    && selectedAdditionalSponsorships.length > 0;

  const shouldShowSponsorOnlyWarning =
    sponsorModalPlanType === 'sponsor'
    && hasAnySponsorSelection;

  // Sponsor tier data with bullet points
  const sponsorTiers = [
    {
      id: 'platinum',
      name: 'Platinum Sponsor',
      price: 25000,
      savings: 1200,
      description: 'Two foursomes included',
      benefits: [
        'Two foursomes of golf included',
        'Logo/Name featured on banners at dinner and lunch halfway house',
        'Four signs with your logo placed on the course',
        'Logo displayed in awards presentations',
        'Logo on event itinerary',
        'Option for a sponsored hole'
      ]
    },
    {
      id: 'dinner',
      name: 'Dinner Sponsor',
      price: 15000,
      savings: 600,
      description: 'One foursome included',
      benefits: [
        'One foursome of golf included',
        'Logo/Name featured prominently at dinner event',
        'Two signs with your logo placed on the course',
        'Logo displayed in dinner presentations',
        'Logo on event itinerary',
        'Recognition during dinner announcements'
      ]
    },
    {
      id: 'lunch',
      name: 'Lunch Sponsor',
      price: 10000,
      savings: 600,
      description: 'One foursome included',
      benefits: [
        'One foursome of golf included',
        'Logo/Name featured at lunch halfway house',
        'Two signs with your logo placed on the course',
        'Logo displayed in lunch presentations',
        'Logo on event itinerary',
        'Recognition during lunch announcements'
      ]
    }
  ];

  // À la carte options
  const alaCarteOptions = [
    {
      id: 'add-golfers',
      name: 'Add Golfers',
      price: 150,
      description: 'Additional golfer registration',
      benefits: [
        '18-hole scramble format',
        'Cart, greens fees & amenities included',
        'Food, drinks & player perks',
        'Contests, prizes & awards'
      ]
    },
    {
      id: 'dinner-only',
      name: 'Dinner Only',
      price: 50,
      description: 'Evening reception and dinner',
      benefits: [
        'Post-tournament dinner included',
        'Awards ceremony attendance',
        'Social hour & networking',
        'Access to silent auction',
        'Event swag and materials'
      ]
    },
    {
      id: 'silent-auction',
      name: 'Silent Auction Donation',
      price: 0,
      description: 'Donate items for silent auction',
      benefits: [
        'Recognition as auction donor',
        'Company logo on auction materials',
        'Promotional materials at event',
        'Tax-deductible donation receipt',
        'Optional dinner guest tickets available'
      ]
    },
    {
      id: 'monetary-donation',
      name: 'Monetary Donation',
      price: 0,
      description: 'Make a monetary contribution',
      benefits: [
        'Recognition as event sponsor',
        'Tax-deductible donation receipt',
        'Company acknowledgment materials',
        'Optional dinner guest tickets available',
        'Special donor recognition'
      ]
    }
  ];

  // Quantity options for secondary screens
  const getQuantityOptions = (type: string) => {
    if (type === 'add-golfers') {
      return [
        { id: '1-player', label: '1 Player', price: 150 },
        { id: '2-players', label: '2 Players', price: 300 },
        { id: '3-players', label: '3 Players', price: 450 },
        { id: '4-players', label: '4 Players', price: 600 }
      ];
    } else if (type === 'dinner-only') {
      return [
        { id: '1-guest', label: '1 Guest', price: 50 },
        { id: '2-guests', label: '2 Guests', price: 100 },
        { id: '3-guests', label: '3 Guests', price: 150 },
        { id: '4-guests', label: '4 Guests', price: 200 }
      ];
    }
    return [];
  };

  // Additional sponsorship options
  const additionalSponsorships = [
    { 
      id: 'beverage-cart', 
      name: 'Beverage Cart Sponsor', 
      price: 5000, 
      description: 'Mobile beverage service throughout the course',
      benefits: [
        'Logo/name displayed on all beverage carts',
        'Option for sponsored hole'
      ]
    },
    { 
      id: 'bloody-mary', 
      name: 'Bloody Mary Breakfast Sponsor', 
      price: 5000, 
      description: 'Sponsor the pre-tournament breakfast beverage station',
      benefits: [
        'Logo/name displayed at breakfast beverage station',
        'Recognition during breakfast announcements'
      ]
    },
    { 
      id: 'food-truck', 
      name: 'Food Truck Sponsor', 
      price: 5000, 
      description: 'On-course mobile food service provider',
      benefits: [
        'Logo/name displayed at the food truck',
        'Option for a table at the food truck'
      ]
    },
    { 
      id: 'air-cannon', 
      name: 'Air Cannon Sponsor', 
      price: 3000, 
      description: 'Sponsor the exciting tee shot air cannon experience',
      benefits: [
        'Logo/name displayed at the air cannon hole'
      ]
    },
    { 
      id: 'cigar-bar', 
      name: 'Cigar Bar Sponsor', 
      price: 3000, 
      description: 'Premium cigar station for participants',
      benefits: [
        'Logo/name displayed at the cigar bar'
      ]
    },
    { 
      id: 'photography', 
      name: 'Photography Sponsor', 
      price: 3000, 
      description: 'Professional event photography services',
      benefits: [
        'Logo/name displayed on the photo frames alongside Crash Champions'
      ]
    },
    { 
      id: 'raffle-prize', 
      name: 'Raffle Prize Sponsor', 
      price: 3000, 
      description: 'Provide prizes for tournament raffle drawings',
      benefits: [
        'Logo/name displayed on tables at reception'
      ]
    },
    { 
      id: 'beverage-station', 
      name: 'Beverage Station Sponsor', 
      price: 3000, 
      description: 'Fixed beverage station on the course',
      benefits: [
        'Logo/name display on bars at reception'
      ]
    },
    { 
      id: 'golf-cart', 
      name: 'Golf Cart Sponsor', 
      price: 3000, 
      description: 'Tournament golf cart fleet sponsorship',
      benefits: [
        'Logo/name displayed on golf carts'
      ]
    },
    { 
      id: 'premier-hole', 
      name: 'Premier Hole Sponsor', 
      price: 2000, 
      description: 'Sponsor a featured hole with signage and setup',
      benefits: [
        'Logo/name displayed on hole and vendor setup'
      ]
    },
    { 
      id: 'driving-range', 
      name: 'Driving Range Sponsor', 
      price: 1500, 
      description: 'Practice facility sponsorship and signage',
      benefits: [
        'Logo/name display at driving range'
      ]
    },
    { 
      id: 'closest-pin', 
      name: 'Closest to the Pin Sponsor', 
      price: 1500, 
      description: 'Sponsor the closest to pin contest',
      benefits: [
        'Logo/name displayed on hole'
      ]
    },
    { 
      id: 'longest-drive', 
      name: 'Longest Drive Sponsor', 
      price: 1500, 
      description: 'Sponsor the longest drive competition',
      benefits: [
        'Logo/name displayed on hole'
      ]
    }
  ];

  // Helper functions for à la carte display
  const getAlaCarteSummaryLabel = (alaCarteItem: any) => {
    if (!alaCarteItem) return 'À la carte Selection';
    
    switch (alaCarteItem.id) {
      case 'add-golfers':
        return 'Total Golfers';
      case 'dinner-only':
        return 'Total Dinner Guests';
      case 'silent-auction':
        return 'Total Items';
      case 'monetary-donation':
        return 'Total Monetary Funds';
      default:
        return 'À la carte Selection';
    }
  };

  // Get original quantity selected (for slot limits)
  const getOriginalAlaCarteQuantity = (alaCarteItem: any, quantityData: any) => {
    if (!alaCarteItem) return 1;
    
    switch (alaCarteItem.id) {
      case 'add-golfers':
        if (quantityData?.id === '1-player') return 1;
        if (quantityData?.id === '2-players') return 2;
        if (quantityData?.id === '3-players') return 3;
        if (quantityData?.id === '4-players') return 4;
        return 1;
      case 'dinner-only':
        if (quantityData?.id === '1-guest') return 1;
        if (quantityData?.id === '2-guests') return 2;
        if (quantityData?.id === '3-guests') return 3;
        if (quantityData?.id === '4-guests') return 4;
        return 1;
      default:
        return 1;
    }
  };

  const getAlaCarteCount = (alaCarteItem: any, quantityData: any) => {
    if (!alaCarteItem) return '1';
    
    switch (alaCarteItem.id) {
      case 'add-golfers':
        // Return the pre-selected quantity count
        return getOriginalAlaCarteQuantity(alaCarteItem, quantityData).toString();
      case 'dinner-only':
        // Return the pre-selected quantity count
        return getOriginalAlaCarteQuantity(alaCarteItem, quantityData).toString();
      case 'silent-auction':
        return '1';
      case 'monetary-donation':
        return '1';
      default:
        return '1';
    }
  };

  // Show toast notification
  const showToastNotification = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 5000); // Auto hide after 5 seconds
  };

  // Scroll to field with error
  const scrollToField = (fieldId: string) => {
    // Map field IDs to actual DOM element selectors
    const fieldSelectors: {[key: string]: string} = {
      'contactFirstName': 'input[name="contactFirstName"]',
      'contactLastName': 'input[name="contactLastName"]', 
      'contactEmail': 'input[name="contactEmail"]',
      'contactPhone': 'input[name="contactPhone"]',
      'companyName': 'input[name="companyName"]',
      'teamName': 'input[name="teamName"]',
      'partyName': 'input[name="partyName"]'
    };
    
    // Handle player fields
    if (fieldId.startsWith('player')) {
      fieldSelectors[fieldId] = `input[name="${fieldId}"]`;
    }
    
    // Handle guest fields  
    if (fieldId.startsWith('guest')) {
      fieldSelectors[fieldId] = `input[name="${fieldId}"]`;
    }
    
    const selector = fieldSelectors[fieldId];
    if (selector) {
      const element = document.querySelector(selector) as HTMLElement;
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center'
        });
        // Focus the field after scrolling
        setTimeout(() => element.focus(), 500);
      }
    }
  };

  // Validate all required form fields
  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    let hasErrors = false;
    let firstErrorField: string | null = null;
    
    // Always required: Primary contact info
    if (!contactInfo.firstName.trim()) {
      errors['contactFirstName'] = 'First Name is required';
      if (!firstErrorField) firstErrorField = 'contactFirstName';
      hasErrors = true;
    } else if (!nameRegex.test(contactInfo.firstName.trim())) {
      errors['contactFirstName'] = 'First Name can only include letters, spaces, hyphens, or apostrophes';
      if (!firstErrorField) firstErrorField = 'contactFirstName';
      hasErrors = true;
    } else if (contactInfo.firstName.trim().length > 20) {
      errors['contactFirstName'] = 'First Name must be 20 characters or less';
      if (!firstErrorField) firstErrorField = 'contactFirstName';
      hasErrors = true;
    }
    if (!contactInfo.lastName.trim()) {
      errors['contactLastName'] = 'Last Name is required';
      if (!firstErrorField) firstErrorField = 'contactLastName';
      hasErrors = true;
    } else if (!nameRegex.test(contactInfo.lastName.trim())) {
      errors['contactLastName'] = 'Last Name can only include letters, spaces, hyphens, or apostrophes';
      if (!firstErrorField) firstErrorField = 'contactLastName';
      hasErrors = true;
    }
    if (!contactInfo.email.trim()) {
      errors['contactEmail'] = 'Email is required';
      if (!firstErrorField) firstErrorField = 'contactEmail';
      hasErrors = true;
    } else if (!emailRegex.test(contactInfo.email.trim())) {
      errors['contactEmail'] = 'Email must be valid and end in .com, .org, or .net';
      if (!firstErrorField) firstErrorField = 'contactEmail';
      hasErrors = true;
    }
    if (!contactInfo.phone.trim()) {
      errors['contactPhone'] = 'Phone is required';
      if (!firstErrorField) firstErrorField = 'contactPhone';
      hasErrors = true;
    } else if (!phoneRegex.test(contactInfo.phone.trim())) {
      errors['contactPhone'] = 'Phone must be in format (xxx) xxx-xxxx';
      if (!firstErrorField) firstErrorField = 'contactPhone';
      hasErrors = true;
    }
    
    // Always required: Company info
    if (!companyInfo.companyName.trim()) {
      errors['companyName'] = 'Company Name is required';
      if (!firstErrorField) firstErrorField = 'companyName';
      hasErrors = true;
    } else if (companyInfo.companyName.trim().length > 25) {
      errors['companyName'] = 'Company Name must be 25 characters or fewer';
      if (!firstErrorField) firstErrorField = 'companyName';
      hasErrors = true;
    }
    
    // Team Name is required when golf players have been added OR primary contact is used as player 1
    if ((players.length > 0 || usePrimaryAsPlayer1) && !teamName.trim()) {
      errors['teamName'] = 'Team Name is required';
      if (!firstErrorField) firstErrorField = 'teamName';
      hasErrors = true;
    }

    // Party Name is required when dinner guests have been added OR primary contact is used as guest 1
    if ((dinnerGuests.length > 0 || usePrimaryAsGuest1) && !partyName.trim()) {
      errors['partyName'] = 'Party Name is required';
      if (!firstErrorField) firstErrorField = 'partyName';
      hasErrors = true;
    }
    
    // Only required when players are visible (added to array)
    if (players.length > 0) {
      players.forEach((player, index) => {
        if (!player.firstName.trim()) {
          errors[`player${index}FirstName`] = 'First Name is required';
          if (!firstErrorField) firstErrorField = `player${index}FirstName`;
          hasErrors = true;
        } else if (!nameRegex.test(player.firstName.trim())) {
          errors[`player${index}FirstName`] = 'First Name can only include letters, spaces, hyphens, or apostrophes';
          if (!firstErrorField) firstErrorField = `player${index}FirstName`;
          hasErrors = true;
        } else if (player.firstName.trim().length > 20) {
          errors[`player${index}FirstName`] = 'First Name must be 20 characters or less';
          if (!firstErrorField) firstErrorField = `player${index}FirstName`;
          hasErrors = true;
        }
        if (!player.lastName.trim()) {
          errors[`player${index}LastName`] = 'Last Name is required';
          if (!firstErrorField) firstErrorField = `player${index}LastName`;
          hasErrors = true;
        } else if (!nameRegex.test(player.lastName.trim())) {
          errors[`player${index}LastName`] = 'Last Name can only include letters, spaces, hyphens, or apostrophes';
          if (!firstErrorField) firstErrorField = `player${index}LastName`;
          hasErrors = true;
        }
        if (!player.email.trim()) {
          errors[`player${index}Email`] = 'Email is required';
          if (!firstErrorField) firstErrorField = `player${index}Email`;
          hasErrors = true;
        } else if (!emailRegex.test(player.email.trim())) {
          errors[`player${index}Email`] = 'Email must be valid and end in .com, .org, or .net';
          if (!firstErrorField) firstErrorField = `player${index}Email`;
          hasErrors = true;
        }
        if (!player.phone.trim()) {
          errors[`player${index}Phone`] = 'Phone is required';
          if (!firstErrorField) firstErrorField = `player${index}Phone`;
          hasErrors = true;
        } else if (!phoneRegex.test(player.phone.trim())) {
          errors[`player${index}Phone`] = 'Phone must be in format (xxx) xxx-xxxx';
          if (!firstErrorField) firstErrorField = `player${index}Phone`;
          hasErrors = true;
        }
      });
    }
    
    // Only required when dinner guests are visible (added to array)
    if (dinnerGuests.length > 0) {
      dinnerGuests.forEach((guest, index) => {
        if (!guest.firstName.trim()) {
          errors[`guest${index}FirstName`] = 'First Name is required';
          if (!firstErrorField) firstErrorField = `guest${index}FirstName`;
          hasErrors = true;
        } else if (!nameRegex.test(guest.firstName.trim())) {
          errors[`guest${index}FirstName`] = 'First Name can only include letters, spaces, hyphens, or apostrophes';
          if (!firstErrorField) firstErrorField = `guest${index}FirstName`;
          hasErrors = true;
        } else if (guest.firstName.trim().length > 20) {
          errors[`guest${index}FirstName`] = 'First Name must be 20 characters or less';
          if (!firstErrorField) firstErrorField = `guest${index}FirstName`;
          hasErrors = true;
        }
        if (!guest.lastName.trim()) {
          errors[`guest${index}LastName`] = 'Last Name is required';
          if (!firstErrorField) firstErrorField = `guest${index}LastName`;
          hasErrors = true;
        } else if (!nameRegex.test(guest.lastName.trim())) {
          errors[`guest${index}LastName`] = 'Last Name can only include letters, spaces, hyphens, or apostrophes';
          if (!firstErrorField) firstErrorField = `guest${index}LastName`;
          hasErrors = true;
        }
        if (!guest.email.trim()) {
          errors[`guest${index}Email`] = 'Email is required';
          if (!firstErrorField) firstErrorField = `guest${index}Email`;
          hasErrors = true;
        } else if (!emailRegex.test(guest.email.trim())) {
          errors[`guest${index}Email`] = 'Email must be valid and end in .com, .org, or .net';
          if (!firstErrorField) firstErrorField = `guest${index}Email`;
          hasErrors = true;
        }
        if (!guest.phone.trim()) {
          errors[`guest${index}Phone`] = 'Phone is required';
          if (!firstErrorField) firstErrorField = `guest${index}Phone`;
          hasErrors = true;
        } else if (!phoneRegex.test(guest.phone.trim())) {
          errors[`guest${index}Phone`] = 'Phone must be in format (xxx) xxx-xxxx';
          if (!firstErrorField) firstErrorField = `guest${index}Phone`;
          hasErrors = true;
        }
      });
    }
    
    setFieldErrors(errors);
    
    if (hasErrors) {
      showToastNotification('Please complete all required fields');
      // Scroll to the first error field
      if (firstErrorField) {
        setTimeout(() => {
          if (firstErrorField) {
            scrollToField(firstErrorField);
          }
        }, 100);
      }
      return false;
    }
    
    return true;
  };

  // Check if user has remaining slots and should be warned before billing
  const shouldWarnAboutRemainingGolfers = () => {
    // TOP TIER sponsors (platinum/dinner/lunch): warn about remaining free golfer slots
    if (confirmedSponsorTier) {
      if (isTopTierSponsor(confirmedSponsorTier)) {
        return getRemainingGolferSlots() > 0;
      }
    }

    // À la carte add-golfers: warn if they selected X but haven't filled all slots
    if (confirmedAlaCarteItem?.id === 'add-golfers' && showGolfPlayersContent) {
      return getRemainingGolferSlots() > 0;
    }

    // À la carte dinner-only: warn if they opted to add guests but haven't filled all slots
    if (confirmedAlaCarteItem?.id === 'dinner-only' && showDinnerGuestsContent) {
      return getRemainingDinnerSlots() > 0;
    }

    // For additional-sponsorship-only or sponsor-golfer without top tier: skip the warning entirely
    return false;
  };

  // Handle continue to billing logic
  const handleContinueToBilling = () => {
    // Clear previous field errors
    setFieldErrors({});
    
    // First validate all required fields
    if (!validateForm()) {
      return; // Stop if validation fails
    }
    
    // Then check for remaining slots warning
    if (shouldWarnAboutRemainingGolfers()) {
      const isDinnerWarning = confirmedAlaCarteItem?.id === 'dinner-only' && showDinnerGuestsContent && getRemainingDinnerSlots() > 0;
      setRemainingModalType(isDinnerWarning ? 'dinner' : 'golfer');
      setShowRemainingGolfersModal(true);
    } else {
      // Proceed directly to billing
      proceedToBilling();
    }
  };

  const handleChangeTierSelections = () => {
    setShowSponsorForm(false);
    setConfirmedSponsorTier(null);
    setConfirmedAdditionalSponsorships([]);
    setConfirmedAlaCarteItem(null);
    setConfirmedAlaCarteQuantity(null);

    // Reopen the correct selection modal based on original flow.
    if (confirmedAlaCarteItem) {
      setShowAlaCarteModal(true);
    } else {
      setShowSponsorModal(true);
    }
  };

  const handleBackToSelections = () => {
    navigate('/', {
      state: {
        scrollToPricing: true
      }
    });
  };

  // Proceed to billing page
  const proceedToBilling = () => {
    // Debug what we're sending to billing
    console.log('🚀 NAVIGATE TO BILLING - confirmedAlaCarteItem:', confirmedAlaCarteItem);
    
    // Get the summary to debug total cost
    const summary = getRegistrationSummary();
    console.log('🚀 BILLING DEBUG - Registration summary:', summary);
    console.log('🚀 BILLING DEBUG - Total cost:', summary?.totalCost);

    // Navigate to billing page with all form data
    navigate('/billing', {
      state: {
        planType: (location.state as { planType?: string } | null)?.planType,
        confirmedSponsorTier,
        confirmedAdditionalSponsorships,
        confirmedAlaCarteItem,
        confirmedAlaCarteQuantity,
        contactInfo,
        companyInfo,
        teamName,
        partyName,
        usePrimaryAsPlayer1,
        primaryPlayerNeedsGolfClubs,
        primaryPlayerGolfClubHandedness,
        players,
        usePrimaryAsGuest1,
        dinnerGuests,
        specialNotes,
        smsUpdates
      }
    });
  };

  // Helper functions for sponsor benefits
  const getSponsorFreeGolfers = (sponsorTier: any) => {
    if (!sponsorTier) return 0;
    switch (sponsorTier.id) {
      case 'platinum':
        return 8; // Two foursomes
      case 'dinner':
      case 'lunch':
        return 4; // One foursome
      default:
        return 0;
    }
  };

  // Check if this is a sponsor flow (not à la carte)
  const isSponsorFlow = () => {
    const state = location.state as { planType?: string } | null;
    return state?.planType === 'sponsor' || state?.planType === 'sponsor-golfer';
  };

  const isSponsorGolferPlan = () => {
    const state = location.state as { planType?: string } | null;
    return state?.planType === 'sponsor-golfer';
  };

  const isTopTierSponsor = (tier: any) => {
    return !!tier?.id && TOP_TIER_SPONSOR_IDS.includes(tier.id);
  };

  const isSponsorGolferTopTierFlow = () => {
    return isSponsorGolferPlan() && isTopTierSponsor(confirmedSponsorTier);
  };

  const getGolfersPlusCount = () => {
    if (!isSponsorGolferTopTierFlow()) {
      return 0;
    }

    const totalAddedGolfers = (usePrimaryAsPlayer1 ? 1 : 0) + players.length;
    const includedGolfers = getSponsorFreeGolfers(confirmedSponsorTier);
    return Math.max(0, totalAddedGolfers - includedGolfers);
  };

  // Get remaining golfer slots
  const getRemainingGolferSlots = () => {
    let totalSlots = 0;
    
    // If there's a confirmed sponsor tier, use its golfer count
    if (confirmedSponsorTier) {
      totalSlots = getSponsorFreeGolfers(confirmedSponsorTier);
    } 
    // If only additional sponsorships (no primary tier), allow max 4 golfers
    else if (confirmedAdditionalSponsorships.length > 0) {
      totalSlots = 4;
    }
    // For à la carte golfers, use the original quantity selection for slot limits
    else if (confirmedAlaCarteItem?.id === 'add-golfers' && confirmedAlaCarteQuantity) {
      totalSlots = getOriginalAlaCarteQuantity(confirmedAlaCarteItem, confirmedAlaCarteQuantity);
    }
    
    const usedSlots = (usePrimaryAsPlayer1 ? 1 : 0) + players.length;
    return Math.max(0, totalSlots - usedSlots);
  };

  // Get total golfer limit (for display purposes)
  const getTotalGolferLimit = () => {
    if (confirmedSponsorTier) {
      return getSponsorFreeGolfers(confirmedSponsorTier);
    } else if (confirmedAdditionalSponsorships.length > 0) {
      return 4;
    } else if (confirmedAlaCarteItem?.id === 'add-golfers' && confirmedAlaCarteQuantity) {
      // For à la carte golfers, return the original quantity selection for slot limits
      return getOriginalAlaCarteQuantity(confirmedAlaCarteItem, confirmedAlaCarteQuantity);
    }
    return 0;
  };

  // Check if this is additional sponsorship only (no primary sponsor tier)
  const isAdditionalSponsorshipOnly = () => {
    return !confirmedSponsorTier && confirmedAdditionalSponsorships.length > 0;
  };

  // Handle primary contact as Player 1 checkbox
  const handlePrimaryAsPlayer1Change = (checked: boolean) => {
    setUsePrimaryAsPlayer1(checked);
    if (!checked) {
      setPrimaryPlayerNeedsGolfClubs('');
      setPrimaryPlayerGolfClubHandedness('');
    }
  };

  // Add new player
  const addPlayer = () => {
    const newPlayer = {
      id: `player-${Date.now()}`,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      needsGolfClubs: '' as '' | 'yes' | 'no',
      golfClubHandedness: '' as '' | 'left' | 'right'
    };
    setPlayers(prev => [...prev, newPlayer]);
  };

  // Update player information
  const updatePlayer = (id: string, field: string, value: string) => {
    setPlayers(prev => prev.map(player => 
      player.id === id ? { ...player, [field]: value } : player
    ));
  };

  // Format phone number for players
  const formatPlayerPhone = (playerId: string, value: string) => {
    const formatted = formatPhoneNumber(value);
    updatePlayer(playerId, 'phone', formatted);
  };

  // Forfeit player (remove from list)
  const forfeitPlayer = (id: string) => {
    setPlayers(prev => prev.filter(player => player.id !== id));
  };

  // Get total dinner guest limit (for display purposes)
  const getTotalDinnerLimit = () => {
    // For à la carte dinner selections, use the original quantity selection
    if (confirmedAlaCarteItem?.id === 'dinner-only' && confirmedAlaCarteQuantity) {
      return getOriginalAlaCarteQuantity(confirmedAlaCarteItem, confirmedAlaCarteQuantity);
    }
    return 4; // Default max
  };

  // Get remaining dinner guest slots (max 4, or à la carte quantity limit)
  const getRemainingDinnerSlots = () => {
    let totalSlots = 4; // Default max
    
    // For à la carte dinner selections, use the original quantity selection for slot limits
    if (confirmedAlaCarteItem?.id === 'dinner-only' && confirmedAlaCarteQuantity) {
      totalSlots = getOriginalAlaCarteQuantity(confirmedAlaCarteItem, confirmedAlaCarteQuantity);
    }
    
    const usedSlots = (usePrimaryAsGuest1 ? 1 : 0) + dinnerGuests.length;
    return Math.max(0, totalSlots - usedSlots);
  };

  // Handle primary contact as Guest 1 checkbox
  const handlePrimaryAsGuest1Change = (checked: boolean) => {
    setUsePrimaryAsGuest1(checked);
  };

  // Add new dinner guest
  const addDinnerGuest = () => {
    const newGuest = {
      id: `guest-${Date.now()}`,
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    };
    setDinnerGuests(prev => [...prev, newGuest]);
  };

  // Update dinner guest information
  const updateDinnerGuest = (id: string, field: string, value: string) => {
    setDinnerGuests(prev => prev.map(guest => 
      guest.id === id ? { ...guest, [field]: value } : guest
    ));
  };

  // Format phone number for dinner guests
  const formatDinnerGuestPhone = (guestId: string, value: string) => {
    const formatted = formatPhoneNumber(value);
    updateDinnerGuest(guestId, 'phone', formatted);
  };

  // Forfeit dinner guest (remove from list)
  const forfeitDinnerGuest = (id: string) => {
    setDinnerGuests(prev => prev.filter(guest => guest.id !== id));
  };

  // Calculate registration summary
  const getRegistrationSummary = () => {
    if (!showSponsorForm || (!confirmedSponsorTier && confirmedAdditionalSponsorships.length === 0 && !confirmedAlaCarteItem)) {
      return null;
    }

    const additionalSponsors = confirmedAdditionalSponsorships
      .map(id => additionalSponsorships.find(s => s.id === id))
      .filter(Boolean);

    // Determine primary selection (sponsors take priority over à la carte)
    let primarySelection = null;
    let primaryCost = 0;
    let isAlaCarteSelection = false;
    
    if (confirmedSponsorTier) {
      primarySelection = confirmedSponsorTier;
      primaryCost = confirmedSponsorTier.price;
    } else if (additionalSponsors.length > 0) {
      primarySelection = additionalSponsors[0];
      primaryCost = additionalSponsors[0]?.price || 0;
    } else if (confirmedAlaCarteItem) {
      primarySelection = confirmedAlaCarteItem;
      // Different pricing logic for different à la carte items
      if (confirmedAlaCarteItem.id === 'silent-auction' || confirmedAlaCarteItem.id === 'monetary-donation') {
        // These items store custom amount directly on the item
        primaryCost = confirmedAlaCarteItem.customAmount || 0;
        console.log('💰 COST CALCULATION - À la carte item:', confirmedAlaCarteItem.id);
        console.log('💰 COST CALCULATION - Custom amount:', confirmedAlaCarteItem.customAmount);
        console.log('💰 COST CALCULATION - Primary cost:', primaryCost);
      } else if (confirmedAlaCarteItem.id === 'add-golfers') {
        // Use the pre-selected quantity price (matches the pink summary section)
        primaryCost = confirmedAlaCarteQuantity?.price || 0;
      } else if (confirmedAlaCarteItem.id === 'dinner-only') {
        // Use the pre-selected quantity price (matches the pink summary section)
        primaryCost = confirmedAlaCarteQuantity?.price || 0;
      } else {
        // Other à la carte items use quantity-based pricing
        primaryCost = confirmedAlaCarteQuantity?.price || confirmedAlaCarteItem.price || 0;
      }
      isAlaCarteSelection = true;
    }

    const remainingSponsors = confirmedSponsorTier ? additionalSponsors : additionalSponsors.slice(1);
    
    // Add golfers to remaining sponsors if any players were added
    const totalAddedGolfers = (usePrimaryAsPlayer1 ? 1 : 0) + players.length;
    if (totalAddedGolfers > 0 && !isAlaCarteSelection) {
      // Sponsor & Golfer + top-tier sponsors: show included free golfers + paid Golfers Plus.
      if (isSponsorGolferTopTierFlow()) {
        const includedGolfers = getSponsorFreeGolfers(confirmedSponsorTier);
        const freeGolferCount = Math.min(totalAddedGolfers, includedGolfers);
        const golferPlusCount = getGolfersPlusCount();

        if (freeGolferCount > 0) {
          remainingSponsors.push({
            id: 'added-golfers-free',
            name: `Golfers (Included) (${freeGolferCount} player${freeGolferCount !== 1 ? 's' : ''})`,
            description: `${freeGolferCount} golfer spot${freeGolferCount !== 1 ? 's' : ''} included with your sponsorship`,
            price: 0,
            benefits: []
          });
        }

        if (golferPlusCount > 0) {
          remainingSponsors.push({
            id: 'added-golfers-plus',
            name: `Golfers (Plus) (${golferPlusCount} player${golferPlusCount !== 1 ? 's' : ''})`,
            description: `${golferPlusCount} additional player${golferPlusCount !== 1 ? 's' : ''} at $150 per player`,
            price: golferPlusCount * 150,
            benefits: []
          });
        }
      } else {
        // For primary sponsors: golfers are free; for additional-sponsorship-only: golfers are $150 each.
        const golferPrice = confirmedSponsorTier ? 0 : 150;
        const golfersEntry = {
          id: 'added-golfers',
          name: `Golfers (${totalAddedGolfers} players)`,
          description: `${usePrimaryAsPlayer1 ? 'Primary Contact + ' : ''}${players.length} additional player${players.length !== 1 ? 's' : ''} at $150 per player`,
          price: totalAddedGolfers * golferPrice,
          benefits: []
        };
        remainingSponsors.push(golfersEntry);
      }
    }
    
    // Add dinner guests to remaining sponsors if any guests were added
    const totalDinnerGuests = (usePrimaryAsGuest1 ? 1 : 0) + dinnerGuests.length;
    if (totalDinnerGuests > 0 && !isAlaCarteSelection) {
      // Skip this entirely for à la carte selections since they're already included in primary cost
      // Dinner guests are always $50 each (both primary sponsors and additional sponsorships)
      const dinnerGuestsEntry = {
        id: 'added-dinner-guests',
        name: `Dinner Guests (${totalDinnerGuests} guest${totalDinnerGuests !== 1 ? 's' : ''})`,
        description: `${usePrimaryAsGuest1 ? 'Primary Contact + ' : ''}${dinnerGuests.length} additional dinner guest${dinnerGuests.length !== 1 ? 's' : ''} at $50 per guest`,
        price: totalDinnerGuests * 50,
        benefits: []
      };
      remainingSponsors.push(dinnerGuestsEntry);
    }
    
    // Calculate totals
    const totalSponsorsCount = (confirmedSponsorTier ? 1 : 0) + confirmedAdditionalSponsorships.length;
    const additionalCost = remainingSponsors.reduce((sum, sponsor) => sum + (sponsor?.price || 0), 0);
    const totalCost = primaryCost + additionalCost;

    return {
      totalSponsorsCount,
      primarySponsor: primarySelection,
      remainingSponsors,
      primaryCost,
      additionalCost,
      totalCost,
      isAlaCarteSelection,
      alaCarteSummaryLabel: isAlaCarteSelection ? getAlaCarteSummaryLabel(confirmedAlaCarteItem) : null,
      alaCarteCount: isAlaCarteSelection ? getAlaCarteCount(confirmedAlaCarteItem, confirmedAlaCarteQuantity) : null
    };
  };

  const summary = getRegistrationSummary();

  const loadSponsorshipAvailability = useCallback(async () => {
    setIsLoadingSponsorshipAvailability(true);
    try {
      const response = await fetch('/api/register?action=sponsorship-availability');
      const result = await response.json();
      if (result?.success && result?.data && typeof result.data === 'object') {
        setAdditionalSponsorshipAvailability(result.data);
      } else {
        setAdditionalSponsorshipAvailability(DEFAULT_ADDITIONAL_SPONSORSHIP_AVAILABILITY);
      }
    } catch (error) {
      console.warn('Unable to load sponsorship availability, using defaults:', error);
      setAdditionalSponsorshipAvailability(DEFAULT_ADDITIONAL_SPONSORSHIP_AVAILABILITY);
    } finally {
      setIsLoadingSponsorshipAvailability(false);
    }
  }, []);

  // Animate to top of page and initialize selection modals based on nav state.
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    const state = location.state as { planType?: string } | null;
    if (state?.planType === 'sponsor-golfer') {
      setSponsorModalPlanType('sponsor-golfer');
    } else if (state?.planType === 'sponsor') {
      setSponsorModalPlanType('sponsor');
    }

    // Show sponsor modal if this is a sponsor registration
    if (shouldShowSponsorModal()) {
      setShowSponsorModal(true);
    }
    
    // Show à la carte modal if this is an à la carte registration
    if (shouldShowAlaCarteModal()) {
      setShowAlaCarteModal(true);
    }
  }, [location.state, shouldShowSponsorModal, shouldShowAlaCarteModal]);

  useEffect(() => {
    if (showSponsorModal) {
      loadSponsorshipAvailability();
    }
  }, [showSponsorModal, loadSponsorshipAvailability]);

  // Restore form data when navigating back from billing page
  useEffect(() => {
    if (location.state) {
      const stateData = location.state as any;
      
      // Restore all form fields if they exist in the state
      if (stateData.contactInfo) {
        setContactInfo(stateData.contactInfo);
      }
      if (stateData.companyInfo) {
        setCompanyInfo(stateData.companyInfo);
      }
      if (stateData.confirmedSponsorTier) {
        setConfirmedSponsorTier(stateData.confirmedSponsorTier);
        setShowSponsorForm(true);
      }
      if (stateData.confirmedAdditionalSponsorships) {
        setConfirmedAdditionalSponsorships(stateData.confirmedAdditionalSponsorships);
      }
      if (stateData.confirmedAlaCarteItem) {
        setConfirmedAlaCarteItem(stateData.confirmedAlaCarteItem);
        setShowSponsorForm(true);
      }
      if (stateData.confirmedAlaCarteQuantity) {
        setConfirmedAlaCarteQuantity(stateData.confirmedAlaCarteQuantity);
      }
      if (stateData.teamName) {
        setTeamName(stateData.teamName);
      }
      if (stateData.partyName) {
        setPartyName(stateData.partyName);
      }
      if (stateData.primaryPlayerNeedsGolfClubs) {
        setPrimaryPlayerNeedsGolfClubs(stateData.primaryPlayerNeedsGolfClubs);
      }
      if (stateData.primaryPlayerGolfClubHandedness) {
        setPrimaryPlayerGolfClubHandedness(stateData.primaryPlayerGolfClubHandedness);
      }
      if (typeof stateData.usePrimaryAsPlayer1 === 'boolean') {
        setUsePrimaryAsPlayer1(stateData.usePrimaryAsPlayer1);
      }
      if (stateData.players && Array.isArray(stateData.players)) {
        setPlayers(stateData.players);
      }
      if (typeof stateData.usePrimaryAsGuest1 === 'boolean') {
        setUsePrimaryAsGuest1(stateData.usePrimaryAsGuest1);
      }
      if (stateData.dinnerGuests && Array.isArray(stateData.dinnerGuests)) {
        setDinnerGuests(stateData.dinnerGuests);
        if (stateData.dinnerGuests.length > 0 || stateData.usePrimaryAsGuest1) {
          setShowDinnerGuestsContent(true);
        }
      }
      if (stateData.specialNotes) {
        setSpecialNotes(stateData.specialNotes);
      }
      if (typeof stateData.smsUpdates === 'boolean') {
        setSmsUpdates(stateData.smsUpdates);
      }

      // If user returns from billing with existing selections/data, show the details form immediately.
      if (
        stateData.returnFromBilling
        || stateData.confirmedSponsorTier
        || stateData.confirmedAlaCarteItem
        || (Array.isArray(stateData.confirmedAdditionalSponsorships) && stateData.confirmedAdditionalSponsorships.length > 0)
      ) {
        setShowSponsorForm(true);
      }

      if (
        (typeof stateData.usePrimaryAsPlayer1 === 'boolean' && stateData.usePrimaryAsPlayer1)
        || (Array.isArray(stateData.players) && stateData.players.length > 0)
        || !!stateData.teamName
      ) {
        setShowGolfPlayersContent(true);
      }
      
      console.log('🔄 Restored registration data from navigation state');
    }
  }, [location.state]);

  const handleSponsorSelect = (tierId: string) => {
    setSelectedSponsorTier(prev => (prev === tierId ? null : tierId));
  };

  const handleAdditionalSponsorshipToggle = (sponsorshipId: string) => {
    setSelectedAdditionalSponsorships(prev => 
      prev.includes(sponsorshipId)
        ? prev.filter(id => id !== sponsorshipId)
        : [...prev, sponsorshipId]
    );
  };

  const handleAlaCarteItemToggle = (itemId: string) => {
    setSelectedAlaCarteItem(selectedAlaCarteItem === itemId ? null : itemId);
  };

  const handleSponsorDone = () => {
    if (selectedSponsorTier || selectedAdditionalSponsorships.length > 0) {
      // Require explicit golfer choice before closing this modal.
      if (addGolfers === null) {
        setSponsorModalToastMessage('CHOOSE YES OR NO');
        window.setTimeout(() => {
          setSponsorModalToastMessage('');
        }, 2500);
        return;
      }

      // Get sponsor tier data
      const selectedTier = sponsorTiers.find(tier => tier.id === selectedSponsorTier);
      
      // Close modal and show form on same page
      setSponsorModalToastMessage('');
      setShowSponsorModal(false);
      setConfirmedSponsorTier(selectedTier);
      setConfirmedAdditionalSponsorships(selectedAdditionalSponsorships);
      setShowSponsorForm(true);
    }
  };

  const handleSponsorCancel = () => {
    setSponsorModalToastMessage('');
    setShowSponsorModal(false);
    setSelectedSponsorTier(null);
    setSelectedAdditionalSponsorships([]);
    // Navigate back to home page
    navigate('/');
    // Animate to top of page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleAlaCarteDone = () => {
    if (selectedAlaCarteItem === 'add-golfers') {
      // Show Add Golfers form
      setShowAddGolfersForm(true);
    } else if (selectedAlaCarteItem === 'dinner-only') {
      // Show Dinner Only form
      setShowDinnerOnlyForm(true);
    } else if (selectedAlaCarteItem === 'silent-auction') {
      // Show Silent Auction form
      setShowSilentAuctionForm(true);
    } else if (selectedAlaCarteItem === 'monetary-donation') {
      // Show Monetary Donation form
      setShowMonetaryDonationForm(true);
    } else if (selectedAlaCarteItem) {
      // Get the selected à la carte item data
      const selectedItem = alaCarteOptions.find(item => item.id === selectedAlaCarteItem);
      
      setShowAlaCarteModal(false);
      setConfirmedAlaCarteItem(selectedItem);
      setShowSponsorForm(true);
    }
  };

  const handleAlaCarteSecondaryDone = () => {
    if (selectedQuantityOption && currentAlaCarteType) {
      // Get the selected à la carte item and quantity data
      const selectedItem = alaCarteOptions.find(item => item.id === selectedAlaCarteItem);
      const quantityOption = getQuantityOptions(currentAlaCarteType).find(qty => qty.id === selectedQuantityOption);
      
      setShowAlaCarteModal(false);
      setShowAlaCarteSecondary(false);
      setSelectedQuantityOption(null);
      setCurrentAlaCarteType(null);
      setConfirmedAlaCarteItem(selectedItem);
      setConfirmedAlaCarteQuantity(quantityOption);
      setShowSponsorForm(true);
    }
  };

  const handleAlaCarteSecondaryBack = () => {
    setShowAlaCarteSecondary(false);
    setSelectedQuantityOption(null);
    setCurrentAlaCarteType(null);
  };

  const handleSilentAuctionSubmit = async () => {
    if (silentAuctionData.amount && silentAuctionData.description) {
      console.log('🎯 AUCTION SUBMIT - Amount:', silentAuctionData.amount);
      let imageUrls: string[] = [];
      
      // Upload silent auction images to /uploads/auction/ folder if any
      if (silentAuctionData.images.length > 0) {
        try {
          const formData = new FormData();
          silentAuctionData.images.forEach((image, index) => {
            formData.append('images', image);
          });

          const uploadResponse = await fetch('/api/upload-auction-images', {
            method: 'POST',
            body: formData
          });

          const uploadResult = await uploadResponse.json();
          if (uploadResult.success) {
            imageUrls = uploadResult.files.map((file: any) => file.url);
          } else {
            console.error('Image upload failed:', uploadResult.error);
            // Continue without images if upload fails
          }
        } catch (error) {
          console.error('Error uploading images:', error);
          // Continue without images if upload fails
        }
      }
      
      const selectedItem = alaCarteOptions.find(item => item.id === 'silent-auction');
      const parsedAmount = parseFloat(silentAuctionData.amount);
      
      console.log('🎯 AUCTION DEBUG - Raw amount:', silentAuctionData.amount);
      console.log('🎯 AUCTION DEBUG - Parsed amount:', parsedAmount);
      console.log('🎯 AUCTION DEBUG - Selected item:', selectedItem);
      
      const newItem = {
        ...selectedItem,
        customAmount: parsedAmount,
        description: silentAuctionData.description,
        images: silentAuctionData.images,
        imageUrls: imageUrls
      };
      
      console.log('🎯 AUCTION DEBUG - New item with custom amount:', newItem);
      
      setShowAlaCarteModal(false);
      setShowSilentAuctionForm(false);
      setConfirmedAlaCarteItem(newItem);
      console.log('🎯 AUCTION CONFIRMED - Custom amount:', parsedAmount);
      setShowSponsorForm(true);
    }
  };

  const handleAddGolfersSubmit = () => {
    if (selectedQuantityOption) {
      const selectedItem = alaCarteOptions.find(item => item.id === 'add-golfers');
      const quantityOption = getQuantityOptions('add-golfers').find(qty => qty.id === selectedQuantityOption);
      
      setShowAlaCarteModal(false);
      setShowAddGolfersForm(false);
      setSelectedQuantityOption(null);
      setConfirmedAlaCarteItem(selectedItem);
      setConfirmedAlaCarteQuantity(quantityOption);
      setShowSponsorForm(true);
    }
  };

  const handleAddGolfersCancel = () => {
    setShowAddGolfersForm(false);
    setSelectedQuantityOption(null);
  };

  const handleDinnerOnlySubmit = () => {
    if (selectedQuantityOption) {
      const selectedItem = alaCarteOptions.find(item => item.id === 'dinner-only');
      const quantityOption = getQuantityOptions('dinner-only').find(qty => qty.id === selectedQuantityOption);
      
      setShowAlaCarteModal(false);
      setShowDinnerOnlyForm(false);
      setSelectedQuantityOption(null);
      setConfirmedAlaCarteItem(selectedItem);
      setConfirmedAlaCarteQuantity(quantityOption);
      setShowSponsorForm(true);
    }
  };

  const handleDinnerOnlyCancel = () => {
    setShowDinnerOnlyForm(false);
    setSelectedQuantityOption(null);
  };

  const handleMonetaryDonationSubmit = () => {
    if (monetaryDonationAmount) {
      console.log('💰 DONATION SUBMIT - Amount:', monetaryDonationAmount);
      console.log('💰 DONATION SUBMIT - Type of amount:', typeof monetaryDonationAmount);
      const selectedItem = alaCarteOptions.find(item => item.id === 'monetary-donation');
      const parsedAmount = parseFloat(monetaryDonationAmount);
      
      console.log('💰 DONATION DEBUG - Raw amount:', monetaryDonationAmount);
      console.log('💰 DONATION DEBUG - Parsed amount:', parsedAmount);
      console.log('💰 DONATION DEBUG - Type of parsed:', typeof parsedAmount);
      
      const newItem = {
        ...selectedItem,
        customAmount: parsedAmount
      };
      
      console.log('💰 DONATION DEBUG - New item:', newItem);
      console.log('💰 DONATION DEBUG - Custom amount in item:', newItem.customAmount);
      
      setShowAlaCarteModal(false);
      setShowMonetaryDonationForm(false);
      setConfirmedAlaCarteItem(newItem);
      console.log('💰 DONATION CONFIRMED - Custom amount:', parsedAmount);
      setShowSponsorForm(true);
    }
  };

  const handleMonetaryDonationCancel = () => {
    setShowMonetaryDonationForm(false);
    setMonetaryDonationAmount('');
  };

  const handleSilentAuctionCancel = () => {
    setShowSilentAuctionForm(false);
    setSilentAuctionData({ amount: '', description: '', images: [] });
  };

  const handleAuctionImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles: File[] = [];
    const errors: string[] = [];

    // Validate each file
    for (const file of files) {
      console.log('File selected:', file.name, file.size, file.type);

      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name}: Please upload PNG, JPG, or WebP files only.`);
        continue;
      }

      // Validate file size (maximum 3MB)
      const maxSize = 3 * 1024 * 1024; // 3MB in bytes
      if (file.size > maxSize) {
        errors.push(`${file.name}: File size must be 3MB or smaller. Please choose a smaller file or compress your image.`);
        continue;
      }

      // Check if we're at the limit
      if (silentAuctionData.images.length + validFiles.length >= 1) {
        errors.push(`Maximum 1 image allowed. Skipping additional files.`);
        break;
      }

      validFiles.push(file);
    }

    // Show errors if any
    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    // Add valid files to the state
    if (validFiles.length > 0) {
      setSilentAuctionData(prev => ({
        ...prev,
        images: [...prev.images, ...validFiles].slice(0, 5) // Ensure we never exceed 5 images
      }));
    }

    // Clear the input so the same file can be selected again
    e.target.value = '';
  };

  const removeAuctionImage = (index: number) => {
    setSilentAuctionData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleAlaCarteCancel = () => {
    setShowAlaCarteModal(false);
    setSelectedAlaCarteItem(null);
    setShowAlaCarteSecondary(false);
    setSelectedQuantityOption(null);
    setCurrentAlaCarteType(null);
    setShowSilentAuctionForm(false);
    setSilentAuctionData({ amount: '', description: '', images: [] });
    setShowMonetaryDonationForm(false);
    setMonetaryDonationAmount('');
    setShowAddGolfersForm(false);
    setShowDinnerOnlyForm(false);
    setSelectedQuantityOption(null);
    // Navigate back to home page
    navigate('/');
    // Animate to top of page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleGolfPlayersToggle = (checked: boolean) => {
    setShowGolfPlayersContent(checked);
    
    // If unchecking, clear all golf player data
    if (!checked) {
      setPlayers([]);
      setTeamName('');
      setUsePrimaryAsPlayer1(false);
    }
  };

  const handleDinnerGuestsToggle = (checked: boolean) => {
    setShowDinnerGuestsContent(checked);
    
    // If unchecking, clear all dinner guest data
    if (!checked) {
      setDinnerGuests([]);
      setUsePrimaryAsGuest1(false);
      setPartyName('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Animation Styles */}
      <style>{`
        .animate-slide-in {
          animation: slideIn 0.3s ease-out forwards;
        }
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .golf-content-enter {
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: all 0.4s ease-in-out;
        }
        .golf-content-enter-active {
          max-height: 10000px;
          opacity: 1;
        }
        .golf-content-exit {
          max-height: 10000px;
          opacity: 1;
          overflow: hidden;
          transition: all 0.3s ease-in-out;
        }
        .golf-content-exit-active {
          max-height: 0;
          opacity: 0;
        }
      `}</style>
      
      <Navigation />
      
      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-24 right-4 z-50 animate-slide-in">
          <div className="bg-gray-800 text-white px-4 py-3 rounded-lg shadow-lg max-w-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0 mr-3">
                <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
                  <XMarkIcon className="h-4 w-4 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">{toastMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sponsor Modal Switching Overlay */}
      {isSwitchingSponsorModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40">
          <div className="rounded-xl bg-white px-6 py-4 shadow-2xl">
            <p className="text-sm font-semibold text-gray-900">Loading Sponsor &amp; Golfer options...</p>
          </div>
        </div>
      )}
      
      {/* Tournament Header */}
      <div className="bg-white shadow-sm border-b mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {/* Tournament Logo */}
              <div className="mr-6">
                <img 
                  src={`${process.env.PUBLIC_URL}/assets/ChampionsClassicLogo-Final-Black.png`} 
                  alt="Champions Classic 2026" 
                  className="object-cover rounded-lg"
                  style={{ height: '110px', width: '110px' }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Champions Classic 2026</h1>
                <p className="text-lg text-gray-600 mt-2">
                  Thursday, September 10, 2026 @ 8:00am
                </p>
                <p className="text-sm text-red-600 font-medium mt-1">
                  Registration for: {getSelectionName()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Sponsor Tier Selection Modal */}
      {showSponsorModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
              aria-hidden="true"
              onClick={handleSponsorCancel}
            />
            
            {/* Modal Content */}
            <div className="relative transform overflow-hidden rounded-2xl bg-white px-8 pb-8 pt-6 text-left shadow-2xl transition-all w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              {/* Close Button */}
              <button
                onClick={handleSponsorCancel}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              {/* Header */}
              <div className="text-center mb-8">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 mb-4">
                  <StarIcon className="h-8 w-8 text-red-600" aria-hidden="true" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Select Your Sponsor Tier Level
                </h3>
                <p className="text-gray-600">
                  Choose the sponsorship package that best fits your goals
                </p>
              </div>

              {/* TOP TIER SPONSORS Section */}
              <div className="mb-6">
                {(() => {
                  // Hide headline for sponsor-only modal
                  if (sponsorModalPlanType === 'sponsor') {
                    return null;
                  }
                  return (
                    <h4 className="mt-4 text-sm font-semibold text-red-600 uppercase tracking-wide mb-4">
                      TOP TIER SPONSORS
                    </h4>
                  );
                })()}
                
                <div className="space-y-3">
                  {sponsorTiers.filter(tier => {
                    return !(sponsorModalPlanType === 'sponsor' && ['platinum', 'dinner', 'lunch'].includes(tier.id));
                  }).map((tier) => (
                    <div key={tier.id}>
                      {/* Selection Row */}
                      <label 
                        className={`relative flex cursor-pointer rounded-xl border p-4 transition-all duration-200 hover:border-red-300 hover:bg-red-50 ${
                          selectedSponsorTier === tier.id 
                            ? 'border-red-500 bg-red-50' 
                            : 'border-gray-200'
                        }`}
                      >
                        <input
                          type="checkbox"
                          name={`sponsorTier-${tier.id}`}
                          value={tier.id}
                          checked={selectedSponsorTier === tier.id}
                          onChange={() => handleSponsorSelect(tier.id)}
                          className="h-4 w-4 border-red-300 focus:ring-red-500 mt-1 cursor-pointer"
                          style={{
                            accentColor: '#e51c22'
                          }}
                        />
                        <div className="ml-4 flex-1">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="text-lg font-semibold text-gray-900">
                                {tier.name}
                              </div>
                              <div className="hidden sm:block text-sm text-gray-500 mb-1">
                                {tier.description}
                              </div>
                            </div>
                            <div className="text-right ml-4 shrink-0">
                              <div className="text-xl font-bold text-red-600">
                                ${tier.price.toLocaleString()}
                              </div>
                              <div className="text-sm font-medium text-green-600">
                                Save ${tier.savings.toLocaleString()}
                              </div>
                            </div>
                          </div>

                          {/* Benefits full-width below the name/price row */}
                          {selectedSponsorTier === tier.id && (
                            <div className="mt-3">
                              <ul className="space-y-1">
                                {tier.benefits.map((benefit, index) => (
                                  <li key={index} className="flex items-start">
                                    <span className="text-red-600 mr-2 text-sm">•</span>
                                    <span className="text-sm text-gray-700">{benefit}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>

                {/* Additional Event Sponsorships */}
                <div className="mt-2 pt-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
                    <div>
                      <h4 className="text-sm mt-2 font-semibold text-red-600 uppercase tracking-wide mb-2">
                        ADDITIONAL EVENT SPONSORSHIPS
                      </h4>
                      
                    </div>

                    {(() => {
                      if (sponsorModalPlanType !== 'sponsor') {
                        return null;
                      }

                      return (
                        <button
                          type="button"
                          onClick={() => {
                            setIsSwitchingSponsorModal(true);
                            setShowSponsorModal(false);
                            window.setTimeout(() => {
                              setSponsorModalPlanType('sponsor-golfer');
                              setShowSponsorModal(true);
                              setIsSwitchingSponsorModal(false);
                            }, MODAL_SWITCH_DELAY_MS);
                          }}
                          className="text-xs text-red-600 hover:text-red-700 underline hover:font-semibold transition-all text-left sm:text-right self-start sm:self-auto sm:ml-4"
                        >
                          <span className="font-semibold mt-12">Need Platinum, Dinner or Lunch Sponsorship?</span>
                        </button>
                      );
                    })()}
                  </div>

                  {isLoadingSponsorshipAvailability && (
                    <p className="text-xs text-gray-500 mb-3">Loading remaining sponsorship spots...</p>
                  )}
                  
                  <div className="space-y-3">
                    {additionalSponsorships.map((sponsorship) => {
                      const spotsFromApi = additionalSponsorshipAvailability[sponsorship.id] ?? DEFAULT_ADDITIONAL_SPONSORSHIP_AVAILABILITY[sponsorship.id] ?? 0;
                      const isSelected = selectedAdditionalSponsorships.includes(sponsorship.id);
                      const isSoldOut = spotsFromApi <= 0; // Block new selections only when API says 0

                      return (
                      <label 
                        key={sponsorship.id}
                        className={`relative flex rounded-xl border p-4 transition-all duration-200 ${
                          isSelected
                            ? 'border-red-500 bg-red-50'
                            : isSoldOut
                              ? 'border-gray-200 bg-gray-50'
                              : 'border-gray-200 hover:border-red-300 hover:bg-red-50 cursor-pointer'
                        }`}
                      >
                        <input
                          type="checkbox"
                          name={`sponsorship-${sponsorship.id}`}
                          checked={isSelected}
                          onChange={() => {
                            if (spotsFromApi <= 0 && !isSelected) {
                              return;
                            }
                            handleAdditionalSponsorshipToggle(sponsorship.id);
                          }}
                          disabled={spotsFromApi <= 0 && !isSelected}
                          className="h-4 w-4 border-red-300 focus:ring-red-500 mt-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            accentColor: '#e51c22'
                          }}
                        />
                        <div className="ml-4 flex-1">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="text-lg font-semibold text-gray-900">
                                {sponsorship.name}
                              </div>
                              <div className="hidden sm:block text-sm text-gray-500 mb-1">
                                {sponsorship.description}
                              </div>
                            </div>
                            <div className="text-right ml-4 shrink-0">
                              <div className="text-xl font-bold text-red-600">
                                ${sponsorship.price.toLocaleString()}
                              </div>
                              <div className="mt-2 flex items-center justify-end gap-2">
                                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-black text-xs font-bold text-white">
                                  {spotsFromApi}
                                </span>
                                <span className="text-xs text-gray-700">
                                  Spot{spotsFromApi === 1 ? '' : 's'} Remaining
                                </span>
                              </div>
                              {isSoldOut && (
                                <div className="mt-1 text-xs font-semibold text-red-600">Sold Out</div>
                              )}
                            </div>
                          </div>

                          {/* Benefits full-width below the name/price row */}
                          {isSelected && (
                            <div className="mt-3">
                              <ul className="space-y-1">
                                {sponsorship.benefits.map((benefit, index) => (
                                  <li key={index} className="flex items-start">
                                    <span className="text-red-600 mr-2 text-sm">•</span>
                                    <span className="text-sm text-gray-700">{benefit}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </label>
                      );
                    })}
                  </div>
                </div>

                {/* Footer Note */}
                {sponsorModalPlanType !== 'sponsor' && (
                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-500">
                      You can modify player details on the next screen
                    </p>
                  </div>
                )}

                {/* Golfer Section */}
                {(sponsorModalPlanType === 'sponsor-golfer' || (sponsorModalPlanType === 'sponsor' && hasAnySponsorSelection)) && (
                  <div className="bg-gray-800 text-white p-6 rounded-xl mx-0 mt-6 mb-6">
                    <div className="text-left">
                      {(selectedSponsorTier === 'platinum' || selectedSponsorTier === 'dinner' || selectedSponsorTier === 'lunch') && (
                        <div className="mb-4">
                          {selectedSponsorTier === 'platinum' ? (
                            <p className="text-sm">
                              You have <span className="font-semibold text-red-500">8 FREE</span> golfers included as part of your package — a savings of <span className="font-semibold text-green-500">$1,200</span>.
                            </p>
                          ) : (
                            <p className="text-sm">
                              You have <span className="font-semibold text-red-500">4 FREE</span> golfers included as part of your package — a savings of <span className="font-semibold text-green-500">$600</span>.
                            </p>
                          )}
                        </div>
                      )}
                      
                      {shouldShowSponsorGolferWarning && (
                        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                          <div className="flex items-start gap-2">
                            <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-5a1 1 0 00-1 1v2a1 1 0 102 0V9a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <p className="text-sm font-semibold text-amber-900">
                              Your sponsorship came with GOLFER options.
                            </p>
                          </div>
                        </div>
                      )}

                      {shouldShowSponsorOnlyWarning && (
                        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                          <div className="flex items-start gap-2">
                            <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-5a1 1 0 00-1 1v2a1 1 0 102 0V9a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <p className="text-sm font-semibold text-amber-900">
                              Your sponsorship does not come with GOLFER options.
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row gap-3">
                        <label className="flex items-center cursor-pointer bg-gray-700 rounded-lg px-4 py-3 min-w-[80px] justify-center flex-1">
                          <input
                            type={sponsorModalPlanType === 'sponsor' || sponsorModalPlanType === 'sponsor-golfer' ? 'checkbox' : 'radio'}
                            name="golferChoice"
                            checked={addGolfers === true}
                            onChange={() => {
                              if (sponsorModalPlanType === 'sponsor' || sponsorModalPlanType === 'sponsor-golfer') {
                                setAddGolfers(addGolfers === true ? null : true);
                              } else {
                                setAddGolfers(true);
                              }
                              setSponsorModalToastMessage('');
                            }}
                            className="h-4 w-4 text-red-600 border-gray-300 focus:ring-red-500 focus:ring-offset-0 mr-2 cursor-pointer"
                            style={{ accentColor: '#dc2626' }}
                          />
                          <span className="text-sm font-medium text-center">
                            {sponsorModalPlanType === 'sponsor' ? (
                              <>
                                Yes - Add Golfers
                                <br />
                                <span className="text-xs font-normal">(Add Golfer Options to My Sponsorship)</span>
                              </>
                            ) : (
                              <>
                                Yes - Add Golfers
                                <br />
                                <span className="text-xs font-normal">(Add My Golfers Contact Information)</span>
                              </>
                            )}
                          </span>
                        </label>
                        
                        <label className="flex items-center cursor-pointer bg-gray-700 rounded-lg px-4 py-3 justify-center flex-1">
                          <input
                            type={sponsorModalPlanType === 'sponsor' || sponsorModalPlanType === 'sponsor-golfer' ? 'checkbox' : 'radio'}
                            name="golferChoice"
                            checked={addGolfers === false}
                            onChange={() => {
                              if (sponsorModalPlanType === 'sponsor' || sponsorModalPlanType === 'sponsor-golfer') {
                                setAddGolfers(addGolfers === false ? null : false);
                              } else {
                                setAddGolfers(false);
                              }
                              setSponsorModalToastMessage('');
                            }}
                            className="h-4 w-4 text-red-600 border-gray-300 focus:ring-red-500 focus:ring-offset-0 mr-2 cursor-pointer"
                            style={{ accentColor: '#dc2626' }}
                          />
                          <span className="text-sm font-medium text-center">
                            {sponsorModalPlanType === 'sponsor' ? (
                              <>
                                No - Sponsorship Only
                                <br />
                                <span className="text-xs font-normal">(No Golfers Required for this Sponsorship)</span>
                              </>
                            ) : (
                              <>No - Sponsorship Only <br/>
                              <span className="text-xs font-normal">(No Golfers Required for this Sponsorship)</span></>
                            )}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {sponsorModalToastMessage && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                  <p className="text-center text-sm font-semibold text-red-700">{sponsorModalToastMessage}</p>
                </div>
              )}

              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleSponsorDone}
                  disabled={!selectedSponsorTier && selectedAdditionalSponsorships.length === 0}
                  className={`flex-1 px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${
                    selectedSponsorTier || selectedAdditionalSponsorships.length > 0
                      ? 'bg-red-600 text-white hover:bg-red-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  DONE
                </button>
                
                <button
                  onClick={handleSponsorCancel}
                  className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5"
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* À La Carte Modal */}
      {showAlaCarteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto"
             aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="min-h-screen px-4 text-center">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                 onClick={handleAlaCarteCancel}></div>
            
            <span className="inline-block h-screen align-middle" aria-hidden="true">​</span>
            
            <div className="inline-block w-full max-w-2xl my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              {/* Close Button */}
              <button
                onClick={handleAlaCarteCancel}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              {/* Header */}
              <div className="text-center mb-8 pt-8">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 mb-4">
                  <UserGroupIcon className="h-8 w-8 text-red-600" aria-hidden="true" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  À La Carte Options
                </h3>
                <p className="text-gray-600">
                  Build your own tournament experience
                </p>
              </div>

              <div className="px-6 py-4 relative overflow-hidden">
                {/* Primary Screen */}
                <div className={`transition-transform duration-500 ease-in-out ${showAlaCarteSecondary ? '-translate-x-full' : 'translate-x-0'}`}>
                  {/* À La Carte Options */}
                  <div className="space-y-3">
                    {alaCarteOptions.map((option) => (
                      <label 
                        key={option.id}
                        className="relative flex cursor-pointer rounded-xl border p-4 transition-all duration-200 hover:border-red-300 hover:bg-red-50 border-gray-200"
                      >
                        <input
                          type="checkbox"
                          name="alacarte-option"
                          checked={selectedAlaCarteItem === option.id}
                          onChange={() => handleAlaCarteItemToggle(option.id)}
                          className="h-4 w-4 border-red-300 focus:ring-red-500 mt-1 cursor-pointer"
                          style={{
                            accentColor: '#e51c22'
                          }}
                        />
                        <div className="ml-4 flex-1">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="text-lg font-semibold text-gray-900">
                                {option.name}
                              </div>
                              <div className="text-sm text-gray-500 mb-3">
                                {option.description}
                              </div>
                              
                              {/* Benefits inside the selection */}
                              {selectedAlaCarteItem === option.id && (
                                <div className="mt-3">
                                  <ul className="space-y-1">
                                    {option.benefits.map((benefit, index) => (
                                      <li key={index} className="flex items-start">
                                        <span className="text-red-600 mr-2 text-sm">•</span>
                                        <span className="text-sm text-gray-700">{benefit}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                            <div className="text-right ml-4">
                              <div className="text-xl font-bold text-red-600">
                                {option.id === 'silent-auction' ? '$xx' : 
                                 option.id === 'monetary-donation' ? '$x' : 
                                 `$${option.price.toLocaleString()}`}
                              </div>
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Footer Note */}
                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-500">
                      Choose one option for your tournament experience
                    </p>
                  </div>
                </div>

                {/* Secondary Screen */}
                <div className={`absolute inset-0 px-6 py-4 bg-white transition-transform duration-500 ease-in-out ${showAlaCarteSecondary ? 'translate-x-0' : 'translate-x-full'}`}>
                  {currentAlaCarteType && (
                    <>
                      <div className="text-center mb-6">
                        <h4 className="text-xl font-bold text-gray-900 mb-2">
                          {currentAlaCarteType === 'add-golfers' ? 'Select Number of Players' : 'Select Number of Guests'}
                        </h4>
                        <p className="text-gray-600">
                          Choose your quantity
                        </p>
                      </div>

                      <div className="space-y-3">
                        {getQuantityOptions(currentAlaCarteType).map((qty) => (
                          <label 
                            key={qty.id}
                            className={`relative flex cursor-pointer rounded-xl border p-4 transition-all duration-200 hover:border-red-300 hover:bg-red-50 ${
                              selectedQuantityOption === qty.id 
                                ? 'border-red-500 bg-red-50' 
                                : 'border-gray-200'
                            }`}
                          >
                            <input
                              type="radio"
                              name="quantity-option"
                              checked={selectedQuantityOption === qty.id}
                              onChange={() => setSelectedQuantityOption(qty.id)}
                              className="h-4 w-4 border-red-300 focus:ring-red-500 mt-1 cursor-pointer"
                              style={{
                                accentColor: '#e51c22'
                              }}
                            />
                            <div className="ml-4 flex-1">
                              <div className="flex items-center justify-between">
                                <div className="text-lg font-semibold text-gray-900">
                                  {qty.label}
                                </div>
                                <div className="text-xl font-bold text-red-600">
                                  ${qty.price.toFixed(2)}
                                </div>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Silent Auction Screen */}
                <div className={`absolute inset-0 px-6 py-4 bg-white transition-transform duration-500 ease-in-out ${showSilentAuctionForm ? 'translate-x-0' : 'translate-x-full'}`}>
                  <div className="text-center mb-6">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Silent Auction Donation Details
                    </h4>
                    <p className="text-gray-600">
                      Provide details about your auction item
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Auction Amount */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Auction Item Value *
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">$</span>
                        <input
                          type="number"
                          value={silentAuctionData.amount}
                          onChange={(e) => {
                            // Only allow whole numbers, no decimals
                            const value = e.target.value;
                            if (value === '' || /^[0-9]+$/.test(value)) {
                              setSilentAuctionData(prev => ({ ...prev, amount: value }));
                            }
                          }}
                          onKeyDown={(e) => {
                            // Prevent decimal points and other non-numeric characters
                            if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                              e.preventDefault();
                            }
                          }}
                          className="w-full pl-8 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="100"
                          min="1"
                        />
                      </div>
                    </div>

                    {/* Item Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Item Description *
                      </label>
                      <textarea
                        value={silentAuctionData.description}
                        onChange={(e) => setSilentAuctionData(prev => ({ ...prev, description: e.target.value }))}
                        rows={4}
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                        placeholder="Describe your donation item in detail..."
                      />
                    </div>

                    {/* Image Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Item Images <span className="text-gray-500">(Optional — max. 5 images, 3MB each)</span>
                      </label>
                      <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                        <div className="space-y-1 text-center w-full">
                          {silentAuctionData.images.length === 0 ? (
                            <>
                              <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              <div className="flex justify-center text-sm text-gray-600">
                                <label htmlFor="auction-images" className="relative cursor-pointer bg-white rounded-md font-medium text-red-600 hover:text-red-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-red-500">
                                  <span className="text-center">Click to upload auction item images</span>
                                  <input
                                    id="auction-images"
                                    name="auction-images"
                                    type="file"
                                    multiple
                                    accept=".png,.jpg,.jpeg,.webp"
                                    onChange={handleAuctionImageUpload}
                                    className="sr-only"
                                    disabled={silentAuctionData.images.length >= 1}
                                  />
                                </label>
                              </div>
                              <p className="text-xs text-gray-500 text-center">
                                PNG, JPG or WebP — 1 image only, maximum 3MB
                              </p>
                            </>
                          ) : (
                            <>
                              <div className="space-y-4">
                                {/* Image Grid */}
                                <div className="grid grid-cols-2 gap-3">
                                  {silentAuctionData.images.map((image, index) => (
                                    <div key={index} className="relative">
                                      <img
                                        src={URL.createObjectURL(image)}
                                        alt={`Auction item ${index + 1}`}
                                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => removeAuctionImage(index)}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors"
                                      >
                                        ×
                                      </button>
                                      <p className="text-xs text-gray-500 mt-1 truncate">
                                        {image.name} ({(image.size / (1024 * 1024)).toFixed(1)} MB)
                                      </p>
                                    </div>
                                  ))}
                                </div>
                                
                                {/* Add More Button */}
                                {silentAuctionData.images.length < 1 && (
                                  <div className="flex justify-center">
                                    <label htmlFor="auction-images" className="cursor-pointer text-sm text-red-600 hover:text-red-500 underline font-medium">
                                      Add more images ({silentAuctionData.images.length}/1)
                                      <input
                                        id="auction-images"
                                        name="auction-images"
                                        type="file"
                                        multiple
                                        accept=".png,.jpg,.jpeg,.webp"
                                        onChange={handleAuctionImageUpload}
                                        className="sr-only"
                                      />
                                    </label>
                                  </div>
                                )}
                                
                                <p className="text-xs text-green-600 text-center">
                                  ✓ {silentAuctionData.images.length} image{silentAuctionData.images.length !== 1 ? 's' : ''} selected
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Monetary Donation Screen */}
                <div className={`absolute inset-0 px-6 py-4 bg-white transition-transform duration-500 ease-in-out ${showMonetaryDonationForm ? 'translate-x-0' : 'translate-x-full'}`}>
                  <div className="text-center mb-6">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Monetary Donation Amount
                    </h4>
                    <p className="text-gray-600">
                      Enter your donation amount
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Donation Amount *
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">$</span>
                        <input
                          type="number"
                          value={monetaryDonationAmount}
                          onChange={(e) => {
                            // Only allow whole numbers, no decimals
                            const value = e.target.value;
                            if (value === '' || /^[0-9]+$/.test(value)) {
                              setMonetaryDonationAmount(value);
                            }
                          }}
                          onKeyDown={(e) => {
                            // Prevent decimal points and other non-numeric characters
                            if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                              e.preventDefault();
                            }
                          }}
                          className="w-full pl-8 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="30"
                          min="1"
                        />
                      </div>
                      <p className="mt-2 text-sm text-gray-500">
                        Your monetary contribution will support the Champions Classic tournament and related charitable causes.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Add Golfers Screen */}
                <div className={`absolute inset-0 px-6 py-4 bg-white transition-transform duration-500 ease-in-out ${showAddGolfersForm ? 'translate-x-0' : 'translate-x-full'}`}>
                  <div className="text-center mb-6">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Select Number of Players
                    </h4>
                    <p className="text-gray-600">
                      Choose your quantity
                    </p>
                  </div>

                  <div className="space-y-3">
                    {getQuantityOptions('add-golfers').map((qty) => (
                      <label 
                        key={qty.id}
                        className={`relative flex cursor-pointer rounded-xl border p-4 transition-all duration-200 hover:border-red-300 hover:bg-red-50 ${
                          selectedQuantityOption === qty.id 
                            ? 'border-red-500 bg-red-50' 
                            : 'border-gray-200'
                        }`}
                      >
                        <input
                          type="radio"
                          name="quantity-option"
                          checked={selectedQuantityOption === qty.id}
                          onChange={() => setSelectedQuantityOption(qty.id)}
                          className="h-4 w-4 border-red-300 focus:ring-red-500 mt-1 cursor-pointer"
                          style={{
                            accentColor: '#e51c22'
                          }}
                        />
                        <div className="ml-4 flex-1">
                          <div className="flex items-center justify-between">
                            <div className="text-lg font-semibold text-gray-900">
                              {qty.label}
                            </div>
                            <div className="text-xl font-bold text-red-600">
                              ${qty.price.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Dinner Only Screen */}
                <div className={`absolute inset-0 px-6 py-4 bg-white transition-transform duration-500 ease-in-out ${showDinnerOnlyForm ? 'translate-x-0' : 'translate-x-full'}`}>
                  <div className="text-center mb-6">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Select Number of Guests
                    </h4>
                    <p className="text-gray-600">
                      Choose your quantity
                    </p>
                  </div>

                  <div className="space-y-3">
                    {getQuantityOptions('dinner-only').map((qty) => (
                      <label 
                        key={qty.id}
                        className={`relative flex cursor-pointer rounded-xl border p-4 transition-all duration-200 hover:border-red-300 hover:bg-red-50 ${
                          selectedQuantityOption === qty.id 
                            ? 'border-red-500 bg-red-50' 
                            : 'border-gray-200'
                        }`}
                      >
                        <input
                          type="radio"
                          name="quantity-option"
                          checked={selectedQuantityOption === qty.id}
                          onChange={() => setSelectedQuantityOption(qty.id)}
                          className="h-4 w-4 border-red-300 focus:ring-red-500 mt-1 cursor-pointer"
                          style={{
                            accentColor: '#e51c22'
                          }}
                        />
                        <div className="ml-4 flex-1">
                          <div className="flex items-center justify-between">
                            <div className="text-lg font-semibold text-gray-900">
                              {qty.label}
                            </div>
                            <div className="text-xl font-bold text-red-600">
                              ${qty.price.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4 px-6 pb-6 border-t border-gray-200">
                {showAddGolfersForm ? (
                  <>
                    <button
                      onClick={handleAddGolfersCancel}
                      className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5"
                    >
                      BACK
                    </button>
                    <button
                      onClick={handleAddGolfersSubmit}
                      disabled={!selectedQuantityOption}
                      className={`flex-1 px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${
                        selectedQuantityOption
                          ? 'bg-red-600 text-white hover:bg-red-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      DONE
                    </button>
                  </>
                ) : showDinnerOnlyForm ? (
                  <>
                    <button
                      onClick={handleDinnerOnlyCancel}
                      className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5"
                    >
                      BACK
                    </button>
                    <button
                      onClick={handleDinnerOnlySubmit}
                      disabled={!selectedQuantityOption}
                      className={`flex-1 px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${
                        selectedQuantityOption
                          ? 'bg-red-600 text-white hover:bg-red-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      DONE
                    </button>
                  </>
                ) : showMonetaryDonationForm ? (
                  <>
                    <button
                      onClick={handleMonetaryDonationCancel}
                      className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5"
                    >
                      BACK
                    </button>
                    <button
                      onClick={handleMonetaryDonationSubmit}
                      disabled={!monetaryDonationAmount}
                      className={`flex-1 px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${
                        monetaryDonationAmount
                          ? 'bg-red-600 text-white hover:bg-red-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      DONE
                    </button>
                  </>
                ) : showSilentAuctionForm ? (
                  <>
                    <button
                      onClick={handleSilentAuctionCancel}
                      className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5"
                    >
                      BACK
                    </button>
                    <button
                      onClick={handleSilentAuctionSubmit}
                      disabled={!silentAuctionData.amount || !silentAuctionData.description}
                      className={`flex-1 px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${
                        silentAuctionData.amount && silentAuctionData.description
                          ? 'bg-red-600 text-white hover:bg-red-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      DONE
                    </button>
                  </>
                ) : showAlaCarteSecondary ? (
                  <>
                    <button
                      onClick={handleAlaCarteSecondaryBack}
                      className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5"
                    >
                      BACK
                    </button>
                    <button
                      onClick={handleAlaCarteSecondaryDone}
                      disabled={!selectedQuantityOption}
                      className={`flex-1 px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${
                        selectedQuantityOption
                          ? 'bg-red-600 text-white hover:bg-red-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      DONE
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleAlaCarteDone}
                      disabled={!selectedAlaCarteItem}
                      className={`flex-1 px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${
                        selectedAlaCarteItem
                          ? 'bg-red-600 text-white hover:bg-red-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {selectedAlaCarteItem === 'add-golfers' || selectedAlaCarteItem === 'dinner-only' || selectedAlaCarteItem === 'silent-auction' || selectedAlaCarteItem === 'monetary-donation' ? 'CONTINUE' : 'DONE'}
                    </button>
                    
                    <button
                      onClick={handleAlaCarteCancel}
                      className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 hover:shadow-md transform hover:-translate-y-0.5"
                    >
                      CANCEL
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Remaining Golfers Warning Modal */}
      {showRemainingGolfersModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
              aria-hidden="true"
              onClick={() => setShowRemainingGolfersModal(false)}
            />
            
            {/* Modal Content */}
            <div className="relative transform overflow-hidden rounded-2xl bg-white px-8 pb-8 pt-6 text-left shadow-2xl transition-all w-full max-w-lg">
              {/* Close Button */}
              <button
                onClick={() => setShowRemainingGolfersModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              {/* Header */}
              <div className="text-center mb-6 pt-4">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 mb-4">
                  <UserGroupIcon className="h-8 w-8 text-orange-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {remainingModalType === 'dinner' ? 'Remaining Dinner Spots' : 'Remaining Golf Spots'}
                </h3>
              </div>

              {/* Content */}
              <div className="text-center mb-8">
                <p className="text-gray-700 mb-4">
                  <span className="font-semibold">{contactInfo.firstName || 'Guest'}</span>, you still have{' '}
                  <span className="font-bold text-red-600">
                    {remainingModalType === 'dinner' ? getRemainingDinnerSlots() : getRemainingGolferSlots()}
                  </span> of{' '}
                  <span className="font-bold">
                    {remainingModalType === 'dinner' ? getTotalDinnerLimit() : getTotalGolferLimit()}
                  </span>{' '}
                  {remainingModalType === 'dinner' ? 'dinner guest spots' : 'golfer spots'} remaining.
                </p>
                <p className="text-gray-600 text-sm">
                  {remainingModalType === 'dinner'
                    ? 'Do you wish to continue adding dinner guests or forfeit the remaining spots?'
                    : 'Do you wish to continue adding golfers or forfeit the remaining spots?'}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setShowRemainingGolfersModal(false)}
                  className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  NO - Continue Adding
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRemainingGolfersModal(false);
                    proceedToBilling();
                  }}
                  className="flex-1 px-6 py-3 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  YES - FORFEIT REMAINING
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Registration Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border p-8">
              {/* Header */}
              <div className="flex items-center mb-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 mr-4">
                  <UserGroupIcon className="h-6 w-6 text-red-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Champions Classic Registration</h2>
              </div>

              {/* STAT 2 - Selected Tier Summary (appears when sponsor or à la carte is selected) */}
              {showSponsorForm && (confirmedSponsorTier || confirmedAdditionalSponsorships.length > 0 || confirmedAlaCarteItem) && (
                <div className="bg-red-50 rounded-lg shadow-sm border border-red-200 p-6 mb-8">
                  <h3 className="text-sm font-semibold text-red-600 uppercase tracking-wide mb-4">
                    SELECTED TIER
                  </h3>
                  
                  {/* Display Primary Selection (Sponsor OR À la carte) */}
                  {(() => {
                    // Priority: Sponsors first, then à la carte
                    let primarySelection = null;
                    let isAlaCarteSelection = false;
                    
                    if (confirmedSponsorTier) {
                      primarySelection = confirmedSponsorTier;
                    } else if (confirmedAdditionalSponsorships.length > 0) {
                      primarySelection = additionalSponsorships.find(s => s.id === confirmedAdditionalSponsorships[0]);
                    } else if (confirmedAlaCarteItem) {
                      primarySelection = confirmedAlaCarteItem;
                      isAlaCarteSelection = true;
                    }
                    
                    if (!primarySelection) return null;
                    
                    // For à la carte with custom amount (Silent Auction, Monetary Donation) or quantity, use the appropriate data
                    const displayPrice = isAlaCarteSelection && primarySelection.customAmount 
                      ? primarySelection.customAmount
                      : isAlaCarteSelection && confirmedAlaCarteQuantity 
                        ? confirmedAlaCarteQuantity.price 
                        : primarySelection.price;
                    
                    const displayName = isAlaCarteSelection && confirmedAlaCarteQuantity
                      ? `${primarySelection.name} - ${confirmedAlaCarteQuantity.label}`
                      : primarySelection.name;
                    
                    return (
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h2 className="text-xl font-bold text-gray-900 mb-1">
                            {displayName}
                          </h2>
                          <p className="text-sm text-gray-600 mb-1">
                            {isAlaCarteSelection && primarySelection.id === 'silent-auction' 
                              ? 'Auctioning items for Champions'
                              : primarySelection.description}
                            {primarySelection.savings && (
                              <span> — <span className="text-green-600 font-medium">Saves ${primarySelection.savings.toLocaleString()}</span></span>
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-red-600">
                            ${displayPrice?.toLocaleString() || '0'}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  
                  <button
                    type="button"
                    onClick={handleChangeTierSelections}
                    className="text-sm text-red-600 hover:text-red-800 underline"
                  >
                    Change tier selection(s)
                  </button>
                </div>
              )}

              {/* Primary Contact Section */}
              {showSponsorForm && (confirmedSponsorTier || confirmedAlaCarteItem || confirmedAdditionalSponsorships.length > 0) && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                  <h3 className="text-xl font-semibold text-red-600 mb-6">Primary Contact</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="contactFirstName"
                        required
                        value={contactInfo.firstName}
                        onChange={(e) => {
                          setContactInfo(prev => ({ ...prev, firstName: e.target.value }));
                          if (fieldErrors.contactFirstName && e.target.value.trim()) {
                            setFieldErrors(prev => ({ ...prev, contactFirstName: '' }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="Enter first name"
                      />
                      {fieldErrors.contactFirstName && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.contactFirstName}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="contactLastName"
                        required
                        value={contactInfo.lastName}
                        onChange={(e) => {
                          setContactInfo(prev => ({ ...prev, lastName: e.target.value }));
                          if (fieldErrors.contactLastName && e.target.value.trim()) {
                            setFieldErrors(prev => ({ ...prev, contactLastName: '' }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="Enter last name"
                      />
                      {fieldErrors.contactLastName && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.contactLastName}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="contactEmail"
                        required
                        value={contactInfo.email}
                        onChange={(e) => {
                          setContactInfo(prev => ({ ...prev, email: e.target.value }));
                          if (fieldErrors.contactEmail && e.target.value.trim()) {
                            setFieldErrors(prev => ({ ...prev, contactEmail: '' }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="Enter email address"
                      />
                      {fieldErrors.contactEmail && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.contactEmail}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        name="contactPhone"
                        required
                        value={contactInfo.phone}
                        onChange={(e) => {
                          handlePhoneChange(e);
                          if (fieldErrors.contactPhone && e.target.value.trim()) {
                            setFieldErrors(prev => ({ ...prev, contactPhone: '' }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="(555) 123-4567"
                      />
                      {fieldErrors.contactPhone && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.contactPhone}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Company Information Section */}
              {showSponsorForm && (confirmedSponsorTier || confirmedAlaCarteItem || confirmedAdditionalSponsorships.length > 0) && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                  <h3 className="text-xl font-semibold text-red-600 mb-6">Company Information</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        name="companyName"
                        required
                        maxLength={25}
                        value={companyInfo.companyName}
                        onChange={(e) => {
                          const nextCompanyName = e.target.value.slice(0, 25);
                          setCompanyInfo(prev => ({ ...prev, companyName: nextCompanyName }));
                          if (fieldErrors.companyName && e.target.value.trim()) {
                            setFieldErrors(prev => ({ ...prev, companyName: '' }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="Your company or organization name"
                      />
                      {fieldErrors.companyName && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.companyName}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Logo Upload <span className="text-gray-500">(Optional — max. 3MB)</span>
                      </label>
                      <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                        <div className="space-y-1 text-center w-full">
                          {!companyInfo.logoFile ? (
                            <>
                              <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              <div className="flex justify-center text-sm text-gray-600">
                                <label htmlFor="logo-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-red-600 hover:text-red-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-red-500">
                                  <span className="text-center">Click to upload your company logo</span>
                                  <input
                                    id="logo-upload"
                                    name="logo-upload"
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.svg,.webp"
                                    onChange={handleLogoUpload}
                                    className="sr-only"
                                  />
                                </label>
                              </div>
                              <p className="text-xs text-gray-500 text-center">
                                PNG, JPG, SVG or WebP — maximum 3MB file size
                              </p>
                            </>
                          ) : (
                            <>
                              <div className="flex flex-col items-center space-y-3">
                                <img 
                                  src={URL.createObjectURL(companyInfo.logoFile)} 
                                  alt="Company Logo Preview" 
                                  className="max-h-32 max-w-48 object-contain rounded-lg border border-gray-200"
                                />
                                <p className="text-xs text-green-600">
                                  ✓ {companyInfo.logoFile.name} ({(companyInfo.logoFile.size / (1024 * 1024)).toFixed(1)} MB)
                                </p>
                                <label htmlFor="logo-upload" className="cursor-pointer text-xs text-red-600 hover:text-red-500 underline">
                                  Change logo
                                  <input
                                    id="logo-upload"
                                    name="logo-upload"
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.svg,.webp"
                                    onChange={handleLogoUpload}
                                    className="sr-only"
                                  />
                                </label>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Event Options Section */}
              {showSponsorForm && (((confirmedSponsorTier || confirmedAdditionalSponsorships.length > 0) && isSponsorFlow()) || confirmedAlaCarteItem?.id === 'add-golfers' || confirmedAlaCarteItem?.id === 'dinner-only') && (
                <div className="bg-[#fafafa] rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                  <h3 className="text-xl font-semibold text-red-600 mb-6">Additional Event Options</h3>
                  <p className="text-sm text-gray-700 mb-4 italic">*Where applicable: For Event Sponsors, you can add golfers, dinner guests or other sponsorships to your package.</p>
                  
                  <div className="space-y-6">
                    {/* Opted Out Message - Show when user chose "NO - Sponsorship ONLY" */}
                    {addGolfers === false && confirmedSponsorTier && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <div className="text-amber-600">
                              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-amber-800">
                              PLEASE NOTE: You have chosen to OPT OUT from any additional golfers in your package.
                            </p>
                            <button
                              type="button"
                              onClick={() => setAddGolfers(true)}
                              className="mt-2 text-sm bg-amber-600 text-white px-3 py-1 rounded hover:bg-amber-700 transition-colors"
                            >
                              Actually, I'd like to OPT IN golfers
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Free Golfers Information - Only show for primary sponsor tiers when adding golfers */}
                    {confirmedSponsorTier && !isAdditionalSponsorshipOnly() && addGolfers === true && (
                      <div className="bg-gray-900 rounded-lg p-4">
                        <div className="flex items-center space-x-3">
                          <span className="text-white text-sm font-medium">You have</span>
                          <div className="flex items-center justify-center w-8 h-8 bg-red-600 rounded-full">
                            <span className="text-white text-sm font-bold">{getSponsorFreeGolfers(confirmedSponsorTier)}</span>
                          </div>
                          <span className="text-white text-sm font-medium">FREE golfers in your sponsorship</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Golf Players Section - Show for SPONSOR & GOLFER only when no top-tier sponsor (platinum/dinner/lunch) is selected */}
                    {(addGolfers === true || confirmedAlaCarteItem?.id === 'add-golfers') && ( 
                    <>
                    <div className="mt-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <input
                          type="checkbox"
                          id="showGolfPlayers"
                          checked={showGolfPlayersContent}
                          onChange={(e) => handleGolfPlayersToggle(e.target.checked)}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded accent-red-600"
                        />
                        <label htmlFor="showGolfPlayers" className="text-lg font-semibold text-gray-900 cursor-pointer">
                          Add Golfer Contact Information
                        </label>
                      </div>
                      
                      <div className={showGolfPlayersContent ? 'golf-content-enter golf-content-enter-active' : 'golf-content-exit golf-content-exit-active'}>
                      {/* Team Name Input */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Team Name *
                        </label>
                        <input
                          type="text"
                          name="teamName"
                          required
                          value={teamName}
                          onChange={(e) => {
                            setTeamName(e.target.value);
                            if (fieldErrors.teamName && e.target.value.trim()) {
                              setFieldErrors(prev => ({ ...prev, teamName: '' }));
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="Enter your team name"
                        />
                        {fieldErrors.teamName && (
                          <p className="mt-1 text-sm text-red-600">{fieldErrors.teamName}</p>
                        )}
                      </div>
                      
                      {/* Primary Contact as Player 1 Checkbox */}
                      <div className="mb-4">
                        <label className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={usePrimaryAsPlayer1}
                            onChange={(e) => handlePrimaryAsPlayer1Change(e.target.checked)}
                            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded accent-red-600"
                          />
                          <span className="text-sm font-medium text-gray-900">
                            Use the Primary Contact as Player 1
                          </span>
                        </label>
                      </div>

                      {usePrimaryAsPlayer1 && (
                        <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4">
                          <p className="text-sm font-semibold text-gray-900 mb-1">
                            Primary Contact (Player 1)
                          </p>
                          <p className="text-xs text-gray-600 mb-3">
                            {contactInfo.firstName} {contactInfo.lastName}
                          </p>

                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Do you need to rent clubs? <span className="text-gray-500 font-normal">(Optional)</span>
                          </label>
                          <div className="flex items-center gap-6 mb-3">
                            <label className="flex items-center gap-2 text-sm text-gray-900">
                              <input
                                type="radio"
                                name="primaryNeedsClubs"
                                checked={primaryPlayerNeedsGolfClubs === 'yes'}
                                onChange={() => setPrimaryPlayerNeedsGolfClubs('yes')}
                                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                                style={{ accentColor: '#dc2626' }}
                              />
                              Yes
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-900">
                              <input
                                type="radio"
                                name="primaryNeedsClubs"
                                checked={primaryPlayerNeedsGolfClubs === 'no'}
                                onChange={() => {
                                  setPrimaryPlayerNeedsGolfClubs('no');
                                  setPrimaryPlayerGolfClubHandedness('');
                                }}
                                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                                style={{ accentColor: '#dc2626' }}
                              />
                              No
                            </label>
                          </div>

                          {primaryPlayerNeedsGolfClubs === 'yes' && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Choose Hand Preference
                                </label>
                                <div className="flex items-center gap-6">
                                  <label className="flex items-center gap-2 text-sm text-gray-900">
                                    <input
                                      type="radio"
                                      name="primaryClubHandedness"
                                      checked={primaryPlayerGolfClubHandedness === 'left'}
                                      onChange={() => setPrimaryPlayerGolfClubHandedness('left')}
                                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                                      style={{ accentColor: '#dc2626' }}
                                    />
                                    Left-Hand
                                  </label>
                                  <label className="flex items-center gap-2 text-sm text-gray-900">
                                    <input
                                      type="radio"
                                      name="primaryClubHandedness"
                                      checked={primaryPlayerGolfClubHandedness === 'right'}
                                      onChange={() => setPrimaryPlayerGolfClubHandedness('right')}
                                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                                      style={{ accentColor: '#dc2626' }}
                                    />
                                    Right-Hand
                                  </label>
                                </div>
                              </div>
                              <p className="text-sm font-medium text-red-700">
                                Rental Cost: $70.00 (picked up at Pro Shop) - due on pick up
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Players List */}
                      {players.length > 0 && (
                        <div className="mt-6 space-y-4">
                          {players.map((player, index) => (
                            <div key={player.id} className="bg-white border border-gray-200 rounded-lg p-6">
                              <div className="flex items-center justify-between mb-4">
                                <h4 className="text-lg font-semibold text-gray-900">
                                  Player {usePrimaryAsPlayer1 ? index + 2 : index + 1}
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => forfeitPlayer(player.id)}
                                  className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50 transition-colors"
                                >
                                  FORFEIT THIS PLAYER
                                </button>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    First Name *
                                  </label>
                                  <input
                                    type="text"
                                    name={`player${index}FirstName`}
                                    value={player.firstName}
                                    onChange={(e) => {
                                      updatePlayer(player.id, 'firstName', e.target.value);
                                      if (fieldErrors[`player${index}FirstName`] && e.target.value.trim()) {
                                        setFieldErrors(prev => ({ ...prev, [`player${index}FirstName`]: '' }));
                                      }
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    placeholder="Enter first name"
                                  />
                                  {fieldErrors[`player${index}FirstName`] && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors[`player${index}FirstName`]}</p>
                                  )}
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Last Name *
                                  </label>
                                  <input
                                    type="text"
                                    name={`player${index}LastName`}
                                    value={player.lastName}
                                    onChange={(e) => {
                                      updatePlayer(player.id, 'lastName', e.target.value);
                                      if (fieldErrors[`player${index}LastName`] && e.target.value.trim()) {
                                        setFieldErrors(prev => ({ ...prev, [`player${index}LastName`]: '' }));
                                      }
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    placeholder="Enter last name"
                                  />
                                  {fieldErrors[`player${index}LastName`] && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors[`player${index}LastName`]}</p>
                                  )}
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email *
                                  </label>
                                  <input
                                    type="email"
                                    name={`player${index}Email`}
                                    value={player.email}
                                    onChange={(e) => {
                                      updatePlayer(player.id, 'email', e.target.value);
                                      if (fieldErrors[`player${index}Email`] && e.target.value.trim()) {
                                        setFieldErrors(prev => ({ ...prev, [`player${index}Email`]: '' }));
                                      }
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    placeholder="Enter email address"
                                  />
                                  {fieldErrors[`player${index}Email`] && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors[`player${index}Email`]}</p>
                                  )}
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Phone *
                                  </label>
                                  <input
                                    type="tel"
                                    name={`player${index}Phone`}
                                    value={player.phone}
                                    onChange={(e) => {
                                      formatPlayerPhone(player.id, e.target.value);
                                      if (fieldErrors[`player${index}Phone`] && e.target.value.trim()) {
                                        setFieldErrors(prev => ({ ...prev, [`player${index}Phone`]: '' }));
                                      }
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    placeholder="(555) 123-4567"
                                  />
                                  {fieldErrors[`player${index}Phone`] && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors[`player${index}Phone`]}</p>
                                  )}
                                </div>

                                <div className="md:col-span-2 mt-1 pt-4 border-t border-gray-100">
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Do you need to rent clubs? <span className="text-gray-500 font-normal">(Optional)</span>
                                  </label>
                                  <div className="flex items-center gap-6 mb-3">
                                    <label className="flex items-center gap-2 text-sm text-gray-900">
                                      <input
                                        type="radio"
                                        name={`player${index}NeedsClubs`}
                                        checked={player.needsGolfClubs === 'yes'}
                                        onChange={() => updatePlayer(player.id, 'needsGolfClubs', 'yes')}
                                        className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                                        style={{ accentColor: '#dc2626' }}
                                      />
                                      Yes
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-gray-900">
                                      <input
                                        type="radio"
                                        name={`player${index}NeedsClubs`}
                                        checked={player.needsGolfClubs === 'no'}
                                        onChange={() => {
                                          updatePlayer(player.id, 'needsGolfClubs', 'no');
                                          updatePlayer(player.id, 'golfClubHandedness', '');
                                        }}
                                        className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                                        style={{ accentColor: '#dc2626' }}
                                      />
                                      No
                                    </label>
                                  </div>

                                  {player.needsGolfClubs === 'yes' && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-3">
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                          Choose Hand Preference
                                        </label>
                                        <div className="flex items-center gap-6">
                                          <label className="flex items-center gap-2 text-sm text-gray-900">
                                            <input
                                              type="radio"
                                              name={`player${index}ClubHandedness`}
                                              checked={player.golfClubHandedness === 'left'}
                                              onChange={() => updatePlayer(player.id, 'golfClubHandedness', 'left')}
                                              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                                              style={{ accentColor: '#dc2626' }}
                                            />
                                            Left-Hand
                                          </label>
                                          <label className="flex items-center gap-2 text-sm text-gray-900">
                                            <input
                                              type="radio"
                                              name={`player${index}ClubHandedness`}
                                              checked={player.golfClubHandedness === 'right'}
                                              onChange={() => updatePlayer(player.id, 'golfClubHandedness', 'right')}
                                              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                                              style={{ accentColor: '#dc2626' }}
                                            />
                                            Right-Hand
                                          </label>
                                        </div>
                                      </div>
                                      <p className="text-sm font-medium text-red-700">
                                        Rental Cost: $70.00 (picked up at Pro Shop) - due on pick up
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Golfer Button - Always at bottom */}
                      {(getRemainingGolferSlots() > 0 || isSponsorGolferTopTierFlow()) && (
                        <div className="mt-6">
                          <button
                            type="button"
                            onClick={addPlayer}
                            className="w-full border-2 border-dashed border-red-300 rounded-lg p-4 text-red-600 hover:border-red-400 hover:bg-red-50 transition-colors"
                          >
                            <div className="flex items-center justify-center space-x-2">
                              <span className="text-lg font-semibold">+</span>
                              <span className="font-medium">
                                {isSponsorGolferTopTierFlow() && getRemainingGolferSlots() === 0
                                  ? 'Add Unlimited Players - $150 per player'
                                  : `Add Golfer (${getRemainingGolferSlots()} of ${getTotalGolferLimit()} remaining) - ${confirmedSponsorTier ? 'FREE' : '$150 per player'}`}
                              </span>
                            </div>
                          </button>
                        </div>
                      )}
                      </div>
                    </div>
                    </>
                    )}

                    {/* Dinner Guests Section - Show for sponsor flows and dinner-only à la carte - Hidden for SPONSOR and SPONSOR & GOLFER planType */}
                    {(!(confirmedAlaCarteItem?.id === 'add-golfers') || confirmedAlaCarteItem?.id === 'dinner-only') && !(location.state?.planType === 'sponsor') && !(location.state?.planType === 'sponsor-golfer') && (
                    <div className={`mt-1 pt-1 ${addGolfers === true ? 'border-t border-gray-200' : ''}`}>

                      {/* Add Dinner Guests toggle - only shown for dinner-only à la carte */}
                      {confirmedAlaCarteItem?.id === 'dinner-only' ? (
                        <>
                          <div className="flex items-center space-x-3 mb-4">
                            <input
                              type="checkbox"
                              id="showDinnerGuests"
                              checked={showDinnerGuestsContent}
                              onChange={(e) => handleDinnerGuestsToggle(e.target.checked)}
                              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded accent-red-600"
                            />
                            <label htmlFor="showDinnerGuests" className="text-lg font-semibold text-gray-900 cursor-pointer">
                              Add Dinner Guests
                            </label>
                          </div>

                          <div className={showDinnerGuestsContent ? 'golf-content-enter golf-content-enter-active' : 'golf-content-exit golf-content-exit-active'}>
                            {/* Party Name Input */}
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Dinner Party Name *
                              </label>
                              <input
                                type="text"
                                name="partyName"
                                required
                                value={partyName}
                                onChange={(e) => {
                                  setPartyName(e.target.value);
                                  if (fieldErrors.partyName && e.target.value.trim()) {
                                    setFieldErrors(prev => ({ ...prev, partyName: '' }));
                                  }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                placeholder="Enter your party name"
                              />
                              {fieldErrors.partyName && (
                                <p className="mt-1 text-sm text-red-600">{fieldErrors.partyName}</p>
                              )}
                            </div>

                            {/* Primary Contact as Guest 1 Checkbox */}
                            <div className="mb-4">
                        <label className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={usePrimaryAsGuest1}
                            onChange={(e) => handlePrimaryAsGuest1Change(e.target.checked)}
                            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded accent-red-600"
                          />
                          <span className="text-sm font-medium text-gray-900">
                            Use the Primary Contact as Guest 1
                          </span>
                        </label>
                      </div>
                      
                      {/* Dinner Guests List */}
                      {dinnerGuests.length > 0 && (
                        <div className="mb-6 space-y-4">
                          {dinnerGuests.map((guest, index) => (
                            <div key={guest.id} className="bg-white border border-gray-200 rounded-lg p-6">
                              <div className="flex items-center justify-between mb-4">
                                <h5 className="text-lg font-semibold text-gray-900">
                                  Dinner Guest {usePrimaryAsGuest1 ? index + 2 : index + 1}
                                </h5>
                                <button
                                  type="button"
                                  onClick={() => forfeitDinnerGuest(guest.id)}
                                  className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50 transition-colors"
                                >
                                  FORFEIT THIS GUEST
                                </button>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    First Name *
                                  </label>
                                  <input
                                    type="text"
                                    name={`guest${index}FirstName`}
                                    value={guest.firstName}
                                    onChange={(e) => {
                                      updateDinnerGuest(guest.id, 'firstName', e.target.value);
                                      if (fieldErrors[`guest${index}FirstName`] && e.target.value.trim()) {
                                        setFieldErrors(prev => ({ ...prev, [`guest${index}FirstName`]: '' }));
                                      }
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    placeholder="Enter first name"
                                  />
                                  {fieldErrors[`guest${index}FirstName`] && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors[`guest${index}FirstName`]}</p>
                                  )}
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Last Name *
                                  </label>
                                  <input
                                    type="text"
                                    name={`guest${index}LastName`}
                                    value={guest.lastName}
                                    onChange={(e) => {
                                      updateDinnerGuest(guest.id, 'lastName', e.target.value);
                                      if (fieldErrors[`guest${index}LastName`] && e.target.value.trim()) {
                                        setFieldErrors(prev => ({ ...prev, [`guest${index}LastName`]: '' }));
                                      }
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    placeholder="Enter last name"
                                  />
                                  {fieldErrors[`guest${index}LastName`] && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors[`guest${index}LastName`]}</p>
                                  )}
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email *
                                  </label>
                                  <input
                                    type="email"
                                    name={`guest${index}Email`}
                                    value={guest.email}
                                    onChange={(e) => {
                                      updateDinnerGuest(guest.id, 'email', e.target.value);
                                      if (fieldErrors[`guest${index}Email`] && e.target.value.trim()) {
                                        setFieldErrors(prev => ({ ...prev, [`guest${index}Email`]: '' }));
                                      }
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    placeholder="Enter email address"
                                  />
                                  {fieldErrors[`guest${index}Email`] && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors[`guest${index}Email`]}</p>
                                  )}
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Phone *
                                  </label>
                                  <input
                                    type="tel"
                                    name={`guest${index}Phone`}
                                    value={guest.phone}
                                    onChange={(e) => {
                                      formatDinnerGuestPhone(guest.id, e.target.value);
                                      if (fieldErrors[`guest${index}Phone`] && e.target.value.trim()) {
                                        setFieldErrors(prev => ({ ...prev, [`guest${index}Phone`]: '' }));
                                      }
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    placeholder="(555) 123-4567"
                                  />
                                  {fieldErrors[`guest${index}Phone`] && (
                                    <p className="mt-1 text-sm text-red-600">{fieldErrors[`guest${index}Phone`]}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Dinner Guest Button */}
                      {getRemainingDinnerSlots() > 0 && (
                        <div className="mt-6">
                          <button
                            type="button"
                            onClick={addDinnerGuest}
                            className="w-full border-2 border-dashed border-red-300 rounded-lg p-4 text-red-600 hover:border-red-400 hover:bg-red-50 transition-colors"
                          >
                            <div className="flex items-center justify-center space-x-2">
                              <span className="text-lg font-semibold">+</span>
                              <span className="font-medium">
                                Add Dinner Guest - $50.00 per guest ({getRemainingDinnerSlots()} of {getTotalDinnerLimit()} remaining)
                              </span>
                            </div>
                          </button>
                        </div>
                      )}

                          </div>{/* end showDinnerGuestsContent animated div */}
                        </>
                      ) : (
                        /* Non-dinner-only flows: show dinner guests section without toggle */
                        <>
                          <h4 className="text-lg font-semibold text-gray-900 mb-4">
                            Dinner Guests <span className="text-sm font-normal text-gray-500">(optional)</span>
                          </h4>
                          {/* Party Name Input (non-toggle path) */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Dinner Party Name *
                            </label>
                            <input
                              type="text"
                              name="partyName"
                              required
                              value={partyName}
                              onChange={(e) => {
                                setPartyName(e.target.value);
                                if (fieldErrors.partyName && e.target.value.trim()) {
                                  setFieldErrors(prev => ({ ...prev, partyName: '' }));
                                }
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                              placeholder="Enter your party name"
                            />
                            {fieldErrors.partyName && (
                              <p className="mt-1 text-sm text-red-600">{fieldErrors.partyName}</p>
                            )}
                          </div>

                          {/* Primary Contact as Guest 1 Checkbox (non-toggle path) */}
                          <div className="mb-4">
                            <label className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={usePrimaryAsGuest1}
                                onChange={(e) => handlePrimaryAsGuest1Change(e.target.checked)}
                                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded accent-red-600"
                              />
                              <span className="text-sm font-medium text-gray-900">
                                Use the Primary Contact as Guest 1
                              </span>
                            </label>
                          </div>
                          {/* Dinner Guests List (non-toggle path) */}
                          {dinnerGuests.length > 0 && (
                            <div className="mb-6 space-y-4">
                              {dinnerGuests.map((guest, index) => (
                                <div key={guest.id} className="bg-white border border-gray-200 rounded-lg p-6">
                                  <div className="flex items-center justify-between mb-4">
                                    <h5 className="text-lg font-semibold text-gray-900">
                                      Dinner Guest {usePrimaryAsGuest1 ? index + 2 : index + 1}
                                    </h5>
                                    <button type="button" onClick={() => forfeitDinnerGuest(guest.id)} className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50 transition-colors">
                                      FORFEIT THIS GUEST
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                                      <input type="text" name={`guest${index}FirstName`} value={guest.firstName} onChange={(e) => { updateDinnerGuest(guest.id, 'firstName', e.target.value); if (fieldErrors[`guest${index}FirstName`] && e.target.value.trim()) setFieldErrors(prev => ({ ...prev, [`guest${index}FirstName`]: '' })); }} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500" placeholder="Enter first name" />
                                      {fieldErrors[`guest${index}FirstName`] && <p className="mt-1 text-sm text-red-600">{fieldErrors[`guest${index}FirstName`]}</p>}
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                                      <input type="text" name={`guest${index}LastName`} value={guest.lastName} onChange={(e) => { updateDinnerGuest(guest.id, 'lastName', e.target.value); if (fieldErrors[`guest${index}LastName`] && e.target.value.trim()) setFieldErrors(prev => ({ ...prev, [`guest${index}LastName`]: '' })); }} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500" placeholder="Enter last name" />
                                      {fieldErrors[`guest${index}LastName`] && <p className="mt-1 text-sm text-red-600">{fieldErrors[`guest${index}LastName`]}</p>}
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                                      <input type="email" name={`guest${index}Email`} value={guest.email} onChange={(e) => { updateDinnerGuest(guest.id, 'email', e.target.value); if (fieldErrors[`guest${index}Email`] && e.target.value.trim()) setFieldErrors(prev => ({ ...prev, [`guest${index}Email`]: '' })); }} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500" placeholder="Enter email address" />
                                      {fieldErrors[`guest${index}Email`] && <p className="mt-1 text-sm text-red-600">{fieldErrors[`guest${index}Email`]}</p>}
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                                      <input type="tel" name={`guest${index}Phone`} value={guest.phone} onChange={(e) => { formatDinnerGuestPhone(guest.id, e.target.value); if (fieldErrors[`guest${index}Phone`] && e.target.value.trim()) setFieldErrors(prev => ({ ...prev, [`guest${index}Phone`]: '' })); }} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500" placeholder="(555) 123-4567" />
                                      {fieldErrors[`guest${index}Phone`] && <p className="mt-1 text-sm text-red-600">{fieldErrors[`guest${index}Phone`]}</p>}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Add Dinner Guest Button (non-toggle path) */}
                          {getRemainingDinnerSlots() > 0 && (
                            <div className="mt-6">
                              <button type="button" onClick={addDinnerGuest} className="w-full border-2 border-dashed border-red-300 rounded-lg p-4 text-red-600 hover:border-red-400 hover:bg-red-50 transition-colors">
                                <div className="flex items-center justify-center space-x-2">
                                  <span className="text-lg font-semibold">+</span>
                                  <span className="font-medium">Add Dinner Guest - $50.00 per guest ({getRemainingDinnerSlots()} of {getTotalDinnerLimit()} remaining)</span>
                                </div>
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    )}

                    {/* Additional Sponsorships Section - Hide for à la carte add-golfers, dinner-only, SPONSOR planType, and SPONSOR & GOLFER planType */}
                    {!(confirmedAlaCarteItem?.id === 'add-golfers') && !(confirmedAlaCarteItem?.id === 'dinner-only') && !(location.state?.planType === 'sponsor') && !(location.state?.planType === 'sponsor-golfer') && (
                    <div className="mt-8 border-t border-gray-200 pt-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">
                        Additional Sponsorships <span className="text-sm font-normal text-gray-500">(optional)</span>
                      </h4>
                      
                      {/* Confirmed Additional Sponsorships List */}
                      {confirmedAdditionalSponsorships.length > 0 && (
                        <div className="mb-6 space-y-4">
                          {confirmedAdditionalSponsorships.map((sponsorshipId, index) => {
                            const sponsorshipData = [
                              ...sponsorTiers,
                              ...additionalSponsorships
                            ].find(s => s.id === sponsorshipId);
                            
                            return (
                              <div key={`${sponsorshipId}-${index}`} className="bg-white border border-gray-200 rounded-lg p-6">
                                <div className="flex items-center justify-between mb-4">
                                  <h5 className="text-lg font-semibold text-gray-900">
                                    {sponsorshipData?.name}
                                  </h5>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setConfirmedAdditionalSponsorships(prev => 
                                        prev.filter((_, i) => i !== index)
                                      );
                                    }}
                                    className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50 transition-colors"
                                  >
                                    REMOVE SPONSORSHIP
                                  </button>
                                </div>
                                <p className="text-sm text-gray-600 mb-3">{sponsorshipData?.description}</p>
                                <div className="text-right">
                                  <span className="text-lg font-bold text-red-600">
                                    ${sponsorshipData?.price?.toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Add Additional Sponsorship Button */}
                      <div className="mt-6">
                        <button
                          type="button"
                          onClick={() => setShowSponsorModal(true)}
                          className="w-full border-2 border-dashed border-red-300 rounded-lg p-4 text-red-600 hover:border-red-400 hover:bg-red-50 transition-colors"
                        >
                          <div className="flex items-center justify-center space-x-2">
                            <span className="text-lg font-semibold">+</span>
                            <span className="font-medium">
                              Add Additional Sponsorship
                            </span>
                          </div>
                        </button>
                      </div>
                    </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Silent Auction Images Section */}
              {confirmedAlaCarteItem && confirmedAlaCarteItem.id === 'silent-auction' && confirmedAlaCarteItem.images && confirmedAlaCarteItem.images.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                  <h3 className="text-xl font-semibold text-red-600 mb-6">
                    Images
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {confirmedAlaCarteItem.images.map((image: File, index: number) => (
                      <div key={index} className="relative group">
                        <img
                          src={URL.createObjectURL(image)}
                          alt={`Auction item ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border border-gray-200 shadow-sm group-hover:shadow-md transition-shadow"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all"></div>
                        <p className="text-xs text-gray-500 mt-1 truncate">
                          {image.name}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  {confirmedAlaCarteItem.description && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Item Description:</label>
                      <textarea
                        value={confirmedAlaCarteItem.description}
                        onChange={(e) => setConfirmedAlaCarteItem((prev: any) => ({
                          ...prev,
                          description: e.target.value
                        }))}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                        placeholder="Enter item description..."
                      />
                      <p className="text-xs text-gray-500 mt-1">This description will appear in your registration summary and billing details.</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Silent Auction Amount Section */}
              {confirmedAlaCarteItem && confirmedAlaCarteItem.id === 'silent-auction' && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                  <h3 className="text-xl font-semibold text-red-600 mb-6">
                    Auction Item Value
                  </h3>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Total Value of Auction Items:</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">$</span>
                      <input
                        type="number"
                        value={confirmedAlaCarteItem.customAmount || ''}
                        onChange={(e) => setConfirmedAlaCarteItem((prev: any) => ({
                          ...prev,
                          customAmount: parseFloat(e.target.value) || 0
                        }))}
                        className="w-full pl-8 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Enter the total value of your silent auction items. You can adjust this amount before submitting.</p>
                  </div>
                </div>
              )}

              {/* Monetary Donation Amount Section */}
              {confirmedAlaCarteItem && confirmedAlaCarteItem.id === 'monetary-donation' && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                  <h3 className="text-xl font-semibold text-red-600 mb-6">
                    Donation Amount
                  </h3>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Donation Amount:</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">$</span>
                      <input
                        type="number"
                        value={confirmedAlaCarteItem.customAmount || ''}
                        onChange={(e) => setConfirmedAlaCarteItem((prev: any) => ({
                          ...prev,
                          customAmount: parseFloat(e.target.value) || 0
                        }))}
                        className="w-full pl-8 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">You can adjust your donation amount before submitting your registration.</p>
                  </div>
                </div>
              )}
              
              {/* Special Requests or Notes Section */}
              <div className="mt-8 border-t border-gray-200 pt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                  Special Requests or Notes
                </h4>
                <p className="text-sm text-gray-600 mb-4 italic">
                  *Any special requirements needed during the tournament
                </p>
                
                <div className="mb-6">
                  <textarea
                    value={specialNotes}
                    onChange={(e) => setSpecialNotes(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    placeholder="Any special accommodations or requests..."
                  />
                </div>

                {/* Continue to Billing Button */}
                <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                  <button
                    type="button"
                    onClick={handleBackToSelections}
                    className="bg-gray-100 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                  >
                    Back to Selections
                  </button>
                  <button
                    type="button"
                    onClick={handleContinueToBilling}
                    className="bg-red-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                  >
                    Continue to Billing
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Registration Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border p-6 sticky top-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Registration Summary</h3>
              
              {summary ? (
                <div className="space-y-4">
                  {/* Total Selections (Sponsorships or À la carte) */}
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-gray-600">
                      {summary.isAlaCarteSelection ? summary.alaCarteSummaryLabel : 'Total Sponsorships'}
                    </span>
                    <span className="text-gray-900 font-medium">
                      {summary.isAlaCarteSelection ? summary.alaCarteCount : summary.totalSponsorsCount}
                    </span>
                  </div>

                  {/* Primary Selection (Sponsor or À la carte) */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900">
                      {summary.isAlaCarteSelection && confirmedAlaCarteQuantity 
                        ? `${summary.primarySponsor?.name} - ${confirmedAlaCarteQuantity.label}`
                        : summary.primarySponsor?.name}
                    </h4>
                    <p className="text-sm text-gray-500">{summary.primarySponsor?.description}</p>
                    {/* Per-unit pricing for à la carte items */}
                    {summary.isAlaCarteSelection && confirmedAlaCarteItem && (
                      <p className="text-xs text-gray-400">
                        {confirmedAlaCarteItem.id === 'add-golfers' && 'x $150 per player'}
                        {confirmedAlaCarteItem.id === 'dinner-only' && 'x $50 per guest'}
                      </p>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">
                        {!summary.isAlaCarteSelection && confirmedSponsorTier && summary.primarySponsor?.id === confirmedSponsorTier.id && (
                          <div className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                            {summary.primarySponsor?.id === 'platinum' ? 'Two foursomes included' : 'One foursome included'}
                            <br />Saves ${summary.primarySponsor?.savings?.toLocaleString()}
                          </div>
                        )}
                      </span>
                      <span className="text-xl font-bold text-gray-900">${summary.primaryCost.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Additional Sponsorships Section */}
                  {summary.remainingSponsors.length > 0 && (
                    <>
                      <div className="pt-4 border-t border-gray-200">
                        <h4 className="font-medium text-gray-900 mb-3">Additional Sponsorships</h4>
                        <div className="space-y-3">
                          {summary.remainingSponsors.map((sponsor) => (
                            <div key={sponsor?.id} className="flex justify-between items-center">
                              <div>
                                <span className="text-sm font-medium text-gray-900">{sponsor?.name}</span>
                                <p className="text-xs text-gray-500">{sponsor?.description}</p>
                              </div>
                              <span className="text-sm font-bold text-gray-900">${sponsor?.price.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Total */}
                  <div className="pt-4 border-t-2 border-gray-300">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">Total</span>
                      <span className="text-xl font-bold text-red-600">${summary.totalCost.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* What's Included Section */}
                  {summary.primarySponsor && (
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <div className="bg-red-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-3">What's Included:</h4>
                        <ul className="space-y-1 text-sm text-gray-700">
                          {summary.primarySponsor.benefits?.map((benefit: string, index: number) => (
                            <li key={index} className="flex items-start">
                              <span className="text-red-600 mr-2">•</span>
                              <span>{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Select sponsorship options to see summary</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RegistrationPage;